// Translation dictionaries for the public marketing pages.
//
// `pt` is the canonical shape; `Dictionary` is derived from it so `en`
// and `es` are checked to have exactly the same keys. Marketing copy
// only — the authenticated app and the legal pages are out of scope for
// this pass.

import type { Locale } from "./config";

const pt = {
  nav: {
    pricing: "Preços",
    platform: "Plataforma",
    contact: "Contato",
    blog: "Blog",
    login: "Entrar",
    startFree: "Começar grátis",
    goToApp: "Ir para o app →",
  },
  blog: {
    meta: {
      title: "Blog — TrueHacking",
      description: "Artigos sobre segurança ofensiva, pentest e gestão de superfície de ataque.",
    },
    eyebrow: "BLOG",
    title: "Blog",
    subtitle: "Segurança ofensiva, pentest e gestão de superfície de ataque — direto de quem constrói a TrueHacking.",
    readMore: "Ler mais →",
    empty: "Nenhum artigo publicado ainda.",
    loading: "Carregando…",
    backToBlog: "← Voltar ao blog",
    notFound: "Artigo não encontrado.",
  },
  footer: {
    blurb:
      "Segurança ofensiva assistida por IA para times que entregam rápido e precisam ficar à frente dos atacantes.",
    product: "Produto",
    company: "Empresa",
    pricing: "Preços",
    privacy: "Política de Privacidade",
    terms: "Termos de Serviço",
    rights: "Todos os direitos reservados.",
  },
  landing: {
    meta: {
      title:
        "TrueHacking — Pentest com IA e Gestão de Superfície de Ataque",
      description:
        "A TrueHacking é um copiloto de pentest com IA: reconhecimento contínuo, gestão da superfície de ataque externa, achados com score CVSS e playbooks de ataque realistas — sem esperar semanas por um relatório de pentest.",
    },
    hero: {
      eyebrow: "COPILOTO DE PENTEST COM IA",
      tagline:
        "Seu Pentester de IA. Atacando os riscos do seu SaaS antes dos hackers.",
      subline:
        "Segurança ofensiva com IA que encontra o que os scanners deixam passar. Reconhecimento contínuo, análise inteligente de vulnerabilidades, Gestão de Superfície de Ataque Externa e playbooks de ataque para um pentest de verdade — sem esperar semanas por um relatório.",
      ctaPrimary: "Começar grátis",
      ctaPrimaryAuthed: "Abrir dashboard →",
      seePricing: "Ver preços",
      socialProof:
        "Usado por times de segurança em finanças, SaaS, PMEs e e-commerce.",
    },
    problem: {
      eyebrow: "O PROBLEMA",
      title: "Você não defende o que não consegue ver.",
      points: [
        "Você não sabe quantos dos seus ativos estão de fato expostos à internet.",
        "Pentests tradicionais são caros, levam semanas e já chegam desatualizados.",
        "Suas ferramentas afogam você em achados de baixa prioridade sem dizer o que corrigir primeiro.",
        "Credenciais, arquivos de configuração e serviços internos vazam por subdomínios esquecidos que você nunca vê.",
        "Você paga por WAFs, EDRs e SOCs mas ainda não consegue responder 'estamos realmente seguros?'",
      ],
    },
    platform: {
      eyebrow: "A PLATAFORMA",
      title: "Uma ferramenta para cobrir toda a sua superfície de ataque",
      subtitle:
        "A TrueHacking automatiza o que um pentester sênior faria manualmente e coloca IA por cima para priorizar e explicar cada achado.",
      features: [
        {
          title: "Reconhecimento contínuo",
          body: "Mapeie sua superfície de ataque externa e monitore-a 24/7. Subdomínios, portas, serviços e ativos esquecidos — descobertos e acompanhados automaticamente.",
        },
        {
          title: "Análise com IA",
          body: "Cada achado é enriquecido com score CVSS, fatores de risco contextuais e um playbook de ataque realista. Sem o ruído de falsos positivos.",
        },
        {
          title: "Simulação de cadeia de exploração",
          body: "Veja exatamente como um atacante encadearia vulnerabilidades de ponta a ponta. Priorize o que realmente importa, não só o que é grave no papel.",
        },
        {
          title: "Relatórios em um clique",
          body: "Resumo executivo, detalhe técnico, comandos de prova de conceito. Pronto para auditores, clientes e a diretoria — em uma exportação.",
        },
      ],
    },
    how: {
      eyebrow: "COMO FUNCIONA",
      title: "Três passos do zero a achados acionáveis",
      steps: [
        {
          title: "Crie um engagement",
          body: "Aponte a TrueHacking para um domínio, faixa de IP ou tenant de nuvem. Escolha um perfil de scan — web, API, rede, nuvem, mobile, IoT ou sistemas de IA/ML.",
        },
        {
          title: "Rode o scan",
          body: "Sete fases automatizadas: recon passivo, descoberta de ativos, fingerprinting, crawling, varredura de vulnerabilidades, validação profunda. Progresso ao vivo enquanto roda.",
        },
        {
          title: "Revise e aja",
          body: "Os achados são correlacionados, pontuados e priorizados. A revisão humana confirma os críticos. Exporte um relatório quando terminar.",
        },
      ],
    },
    ai: {
      eyebrow: "TRUEHACKING AI",
      title: "O cérebro de um pentester sênior em cada achado",
      body: "Cada vulnerabilidade é analisada no contexto: score CVSS v3.1 com justificativa, um playbook de ataque com passos de exploração realistas, comandos de reprodução e remediação adaptada à sua stack. Sem copia-e-cola genérico. Sem spam de falso positivo. Só as três coisas que um pentester diria se tivesse o dia todo.",
    },
    pricing: {
      eyebrow: "PREÇOS",
      title: "Comece grátis. Faça upgrade quando precisar.",
      subtitle:
        "O plano grátis te coloca pra rodar sem cartão de crédito. Sem surpresas por usuário — o preço escala com scans e recursos.",
      loading: "Carregando planos…",
      perMonth: "por mês",
      custom: "Sob consulta",
      mostPopular: "Mais popular",
      engagements: "{n} projetos",
      scans: "{n} scans / mês",
      users: "{n} usuários",
      unlimited: "Ilimitado",
      ctaFree: "Começar grátis",
      ctaEnterprise: "Falar com vendas",
      ctaGet: "Assinar {name}",
      monthly: "Mensal",
      annual: "Anual",
      save: "2 meses grátis",
      perYear: "por ano",
    },
    featureLabels: {
      ai_enrichment: "Análise de risco com IA",
      ai_senior_pentester: "Pentester Sênior de IA",
      deep_validation: "Validação profunda (SQLMap, testes ativos)",
      support_low: "Suporte de baixa prioridade",
      support_5x8: "Suporte 5x8",
      support_high: "Suporte de alta prioridade",
      sso: "Login único (SSO)",
    },
    finalCta: {
      title: "Pronto para ver sua superfície de ataque real?",
      body: "Crie uma conta em trinta segundos. Rode seu primeiro scan em cinco minutos. Comece grátis — sem cartão de crédito, sem ligação com vendedor.",
      button: "Começar grátis →",
      buttonAuthed: "Abrir dashboard →",
    },
  },

  platform: {
    meta: {
      title: "Plataforma — TrueHacking",
      description: "Do recon ao relatório: descoberta de superfície, exposições vazadas, validação ativa autorizada, correlação, achados explicados por IA e relatórios prontos para o cliente.",
    },
    hero: {
      eyebrow: "A PLATAFORMA",
      title: "Uma plataforma de segurança ofensiva, do recon ao relatório.",
      subline: "Reunimos o melhor arsenal ofensivo e colocamos IA por cima — para descobrir, validar e explicar riscos reais, com você no controle de cada teste ativo.",
    },
    capabilities: {
      eyebrow: "CAPACIDADES",
      title: "Tudo que você precisa para um engagement completo.",
      items: [
        { title: "Mapeie toda a superfície de ataque", body: "Antes de tocar em qualquer coisa, a plataforma descobre o que está exposto: subdomínios, DNS, portas abertas, tecnologias em uso e presença de WAF." },
        { title: "Encontre o que já vazou", body: "Segredos esquecidos em repositórios, e-mails em vazamentos conhecidos, buckets abertos e typosquatting — tudo que um atacante encontraria primeiro." },
        { title: "Valide de verdade — só quando você autoriza", body: "Testes ativos (como SQLMap) rodam apenas dentro do escopo do engagement e somente após a sua autorização explícita." },
        { title: "Muitas ferramentas, uma fonte de verdade", body: "O arsenal todo alimenta um correlation engine que deduplica e conecta os resultados em uma visão única, sem ruído." },
        { title: "Findings que se explicam sozinhos", body: "A IA reescreve cada achado com descrição, impacto e remediação. O AI Senior Pentester valida e orienta a confirmação, no nível de um consultor sênior." },
        { title: "Relatório executivo, pronto para entregar", body: "Risk rating, breakdown por severidade, evidências e apêndice técnico — formatado e exportável. Do achado ao PDF do cliente sem retrabalho." },
        { title: "Vários clientes, contas isoladas", body: "Orgs e workspaces separados, papéis por usuário e painel admin. SSO no Enterprise. Cada cliente na sua caixa, sem vazamento entre contas." },
      ],
    },
    workflow: {
      eyebrow: "O FLUXO",
      title: "Como um engagement acontece",
      steps: [
        "Defina o escopo e os alvos autorizados.",
        "A superfície é mapeada e as exposições, levantadas.",
        "Testes ativos só após a sua autorização.",
        "Cada achado vira um finding com impacto e remediação.",
        "Você confirma o que entra no relatório.",
        "Exporte em PDF, pronto para o cliente.",
      ],
    },
    control: {
      eyebrow: "CONTROLE",
      title: "Poder ofensivo, sempre sob o seu comando.",
      items: [
        { title: "Autorização explícita", body: "Testes ativos só rodam quando você autoriza, por engagement." },
        { title: "Human-in-the-loop", body: "Nada entra no relatório sem a sua revisão e aprovação." },
        { title: "Isolamento por conta", body: "Orgs e workspaces isolados, com papéis e painel admin." },
      ],
    },
    cta: {
      eyebrow: "COMECE AGORA",
      title: "Veja a plataforma em ação.",
      body: "Comece no plano gratuito ou agende uma demonstração.",
      button: "Começar grátis →",
    },
  },

  pricing: {
    meta: { title: "Preços — TrueHacking", description: "Preço que escala com você, não com o seu time. Comece grátis, sem cartão. Pague por scans e recursos, não por assento." },
    hero: { eyebrow: "PREÇOS", title: "Preço que escala com você, não com o seu time.", lead: "Comece de graça, sem cartão. Sem surpresa por assento — você paga por scans e recursos, não por pessoa." },
    comparison: {
      eyebrow: "COMPARAÇÃO",
      title: "Compare os planos em detalhe",
      groupLimits: "Limites",
      groupFeatures: "Recursos",
      engagements: "Projetos",
      scans: "Scans por mês",
      users: "Usuários",
    },
    cta: { eyebrow: "COMECE AGORA", title: "Comece grátis hoje. Faça upgrade quando precisar.", body: "Sem cartão de crédito. Sem surpresa por assento.", buttonFree: "Começar grátis", buttonSales: "Falar com vendas" },
  },

  app: {
    nav: {
      dashboard: "Painel",
      engagements: "Projetos",
      history: "Histórico de scans",
      profile: "Perfil e uso",
      upgrade: "Fazer upgrade",
      admin: "Admin da plataforma",
    },
    adminBadge: "ADMIN",
    logout: "Sair",
    closeMenu: "Fechar menu",
    tagline: "Pense como muitos.\nAtaque como um.",
    aiStatus: "TrueHacking AI",
    services: "Serviços",
    auth: {
      copilot: "Copiloto de Pentest com IA",
      tagline: "Pense como muitos. Ataque como um.",
      signIn: "Entrar",
      createAccount: "Criar conta",
      signInSub: "Acesse seu workspace de pentest",
      registerSub: "Comece seu primeiro workspace",
      twofaTitle: "Verificação em duas etapas",
      twofaHeading: "Digite o código de 6 dígitos",
      twofaHint: "Abra seu app autenticador e digite o código. Você também pode usar um código de backup.",
      codeLabel: "Código",
      codePlaceholder: "000000 ou XXXX-XXXX",
      verify: "Verificar",
      busy: "...",
      backToSignin: "Voltar para o login",
      google: "Continuar com Google",
      github: "Continuar com GitHub",
      or: "ou",
      email: "E-mail",
      emailPlaceholder: "voce@empresa.com",
      password: "Senha",
      passwordPlaceholder: "••••••••",
      fullName: "Nome completo",
      fullNamePlaceholder: "João Silva",
      org: "Nome da organização",
      orgPlaceholder: "Acme Security",
      consentPre: "Concordo com os",
      terms: "Termos de Serviço",
      consentAnd: "e a",
      privacy: "Política de Privacidade",
      consentPost: ", e consinto com o tratamento dos meus dados pessoais conforme descrito. (LGPD Art. 7º, I)",
      needAccount: "Não tem conta? Cadastre-se",
      haveAccount: "Já tem conta? Entrar",
      forgot: "Esqueceu a senha?",
      noOauth: "Provedores OAuth não configurados",
      recoverySubtitle: "Recuperação de senha",
      checkEmail: "Verifique seu e-mail",
      forgotSentPre: "Se existir uma conta com",
      forgotSentPost: ", enviamos um link de redefinição de senha. O link expira em 15 minutos.",
      forgotHeading: "Esqueceu a senha?",
      forgotSub: "Digite seu e-mail e enviaremos um link para redefini-la.",
      sendReset: "Enviar link de redefinição",
      resetSubtitle: "Definir nova senha",
      missingToken: "Token de redefinição ausente. Redirecionando para o login...",
      pwdUpdatedTitle: "Senha atualizada",
      pwdUpdatedBody: "Sua senha foi alterada. Todas as sessões ativas foram desconectadas. Redirecionando para o login...",
      signInNow: "Entrar agora →",
      chooseNewPwd: "Escolha uma nova senha",
      chooseNewPwdSub: "Pelo menos 8 caracteres. Escolha algo memorável.",
      newPassword: "Nova senha",
      confirmPassword: "Confirmar senha",
      updatePassword: "Atualizar senha",
      errPwdShort: "A senha deve ter pelo menos 8 caracteres.",
      errPwdMismatch: "As senhas não coincidem.",
      verifySubtitle: "Verificação de e-mail",
      verifying: "Verificando seu e-mail...",
      verifiedTitle: "✓ E-mail verificado",
      verifiedConfirmed: "está confirmado.",
      verifiedSignedIn: " Você já está conectado — continue para o seu painel.",
      verifiedSignIn: " Entre para continuar.",
      goDashboard: "Ir para o painel →",
      continueSignIn: "Continuar para o login",
      linkInvalidTitle: "Link inválido ou expirado",
      noTokenMsg: "Nenhum token de verificação no link. Abra o link do seu e-mail novamente.",
      invalidMsg: "Este link de verificação é inválido ou expirou. Solicite um novo.",
      requestNewLink: "Solicitar um novo link",
    },
    openMenu: "Abrir menu",
    errors: {
      notFoundCode: "ERRO 404 · NÃO ENCONTRADO",
      notFoundTitle: "Não encontramos essa página.",
      notFoundMsg: "O link pode estar quebrado ou a página pode ter sido movida. Confira a URL ou volte para um ponto de partida conhecido.",
      goHome: "Ir para o início",
      openDashboard: "Abrir painel",
      unavailableCode: "ERRO 503 · SERVIÇO INDISPONÍVEL",
      unavailableTitle: "Estamos com dificuldade para alcançar nossos servidores.",
      unavailableMsg: "A plataforma está temporariamente indisponível — podemos estar implantando uma atualização ou nos recuperando de um incidente. Verificamos a cada 5 segundos e levaremos você de volta assim que voltar ao ar.",
      tryNow: "Tentar agora",
      backDashboard: "Voltar ao painel",
      backHome: "Voltar ao início",
      errorCode: "ERRO 500 · ERRO INESPERADO",
      errorTitle: "Algo deu errado.",
      errorMsg: "Encontramos um problema inesperado ao renderizar esta página. Recarregar costuma resolver; se não, volte ao início e abra novamente.",
      reload: "Recarregar página",
    },
    verifyRequired: {
      vrTitle: "Verifique seu e-mail",
      oneMoreStep: "Mais um passo",
      sentPre: "Enviamos um link de verificação para",
      sentPost: ". Clique nele para concluir a configuração da sua conta e volte para entrar. O link é válido por 3 dias.",
      unsentPre: "Para concluir a configuração da sua conta, clique abaixo para enviar um link de verificação para",
      unsentPost: ". O link será válido por 3 dias.",
      sending: "Enviando...",
      resendIn: "Reenviar em {t}",
      resendEmail: "Reenviar e-mail de verificação",
      sendEmail: "Enviar e-mail de verificação",
      deliveryFailedPre: "Não conseguimos entregar o e-mail agora. Tente novamente em alguns minutos — se continuar falhando, contate",
      deliveryFailedPost: ".",
      coldBody: "Não sabemos qual conta você está tentando verificar neste dispositivo. Entre com as credenciais que você usou no cadastro — se seu e-mail ainda não estiver verificado, você será trazido de volta aqui automaticamente com um botão de Reenviar.",
      goSignIn: "Ir para o login",
      signOutOther: "Sair e usar outra conta",
      backToSignin: "← Voltar para o login",
      hintPre: "Não recebeu? Verifique sua pasta de spam. O link é enviado de",
      hintMid: ". Se você digitou o e-mail errado no cadastro, contate",
      hintPost: ".",
      tooSoon: "Muito cedo — tente novamente em {t}.",
      cantSend: "Não foi possível enviar agora. Tente novamente em alguns minutos — se continuar falhando, contate o suporte.",
      freshLink: "Um novo link está a caminho.",
      couldNotRequest: "Não foi possível solicitar um novo link.",
    },
    dashboard: {
      loading: "Carregando painel...",
      error: "Erro:",
      unknownError: "Erro desconhecido",
      title: "Painel",
      subtitle: "Visão em tempo real da sua plataforma TrueHacking",
      plan: "Plano",
      scansThisMonth: "Scans este mês",
      usageEngagements: "Projetos",
      totalEngagements: "Total de projetos",
      thisWeek: "+{n} esta semana",
      totalFindings: "Total de achados",
      criticalPending: "{n} críticos pendentes",
      evidenceCollected: "Evidências coletadas",
      avgScanDuration: "Duração média do scan",
      successRate: "{n}% de sucesso",
      currentlyScanning: "Escaneando agora ({n})",
      noScans: "Nenhum scan em andamento",
      started: "iniciado",
      recently: "recentemente",
      live: "AO VIVO",
      serviceHealth: "Saúde dos serviços",
      findingsBySeverity: "Achados por severidade",
      pentestTypes: "Tipos de pentest",
      noData: "Sem dados ainda",
      findingsTrend: "Tendência de achados (últimos 14 dias)",
      criticalFindings: "⚠ Achados críticos (revisão pendente)",
      noneGreat: "Nenhum — ótimo!",
      recentEngagements: "Projetos recentes",
      noEngagements: "Nenhum projeto ainda",
      topTools: "Top ferramentas (mais usadas)",
      runs: "{n} execuções",
      executionStats: "Estatísticas de execução",
      totalExecutions: "Total de execuções",
      successful: "Bem-sucedidas",
      errorsTimeouts: "Erros / Timeouts",
      successRateLabel: "Taxa de sucesso",
      errorRate: "Taxa de erro",
    },
    engagements: {
      loading: "Carregando projetos...",
      error: "Erro:",
      unknownError: "Erro desconhecido",
      title: "Projetos",
      subtitle: "{total} no total · {running} em execução (carregados)",
      planLimit: "Limite do plano atingido ({used}/{max}). Faça upgrade ou arquive um.",
      planLimitReached: "🔒 Limite do plano atingido",
      newEngagement: "+ Novo projeto",
      searchPlaceholder: "Buscar por nome, cliente ou alvo…",
      sortNewest: "Ordenar: Mais recentes",
      sortOldest: "Ordenar: Mais antigos",
      sortName: "Ordenar: Nome",
      sortStatus: "Ordenar: Status",
      statusLabel: "Status",
      companyAll: "Empresa: Todas",
      noMatch: "Nenhum projeto corresponde aos filtros atuais.",
      noClient: "Sem cliente",
      showing: "Mostrando {n} de {total}",
      loadMore: "Carregar mais",
      loadingMore: "Carregando…",
      statuses: {
        all: "todos",
        draft: "rascunho",
        active: "ativo",
        scanning: "escaneando",
        review: "revisão",
        retesting: "reteste",
        completed: "concluído",
        archived: "arquivado",
      },
    },
    newEngagement: {
      pickTitle: "Qual tipo de pentest?",
      pickSubtitle: "Isso determina quais ferramentas e estratégias de scan a TrueHacking vai usar",
      bcEngagements: "Projetos",
      bcNew: "Novo",
      detailsTitle: "Pentest de {label}",
      detailsSubtitle: "Configure os detalhes do projeto e os alvos",
      changeType: "← Mudar tipo",
      nameLabel: "Nome do projeto",
      namePlaceholder: "Pentest Externo Q2 2026",
      clientLabel: "Nome do cliente",
      clientPlaceholder: "Acme Corp",
      scopeLabel: "Descrição do escopo",
      scopePlaceholder: "Aplicações web e APIs externas",
      targetsLabel: "Alvos (um por linha)",
      error: "Erro:",
      unknown: "Desconhecido",
      creating: "Criando...",
      createContinue: "Criar e continuar",
      types: {
        web_application: { label: "Aplicação Web", description: "Pentest completo de web app — XSS, SQLi, bypass de auth" },
        api: { label: "Segurança de API", description: "REST/GraphQL — auth, injeção, rate limiting" },
        network: { label: "Rede", description: "Varredura de portas, enumeração de serviços, CVEs conhecidos" },
        cloud: { label: "Nuvem", description: "Buckets S3, serviços expostos, configurações incorretas" },
        mobile: { label: "Mobile", description: "Backend de app mobile — endpoints de API, auth" },
        iot: { label: "IoT", description: "Credenciais padrão, serviços expostos, firmware" },
        ai: { label: "IA/ML", description: "Endpoints de modelo, exposição de dados, prompt injection" },
      },
    },
    engagementDetail: {
      loading: "Carregando projeto...",
      noClient: "Sem cliente",
      created: "Criado em",
      bcEngagements: "Projetos",
      sectionAria: "Seção do projeto",
      tabs: {
        overview: "Visão geral",
        scanning: "⚡ Escaneando...",
        scan: "Scan",
        domains: "Domínios",
        findings: "Achados ({n})",
        evidence: "Evidências",
        retest: "Reteste",
        history: "Histórico de tarefas",
        report: "Relatório",
      },
    },
    severities: {
      critical: "Crítico",
      high: "Alto",
      medium: "Médio",
      low: "Baixo",
      informational: "Info",
    },
    overview: {
      loading: "Carregando visão geral...",
      assets: "Ativos ({n})",
      reviewProgress: "Progresso da revisão",
      needsReview: "Precisa de revisão",
      confirmed: "Confirmado",
      falsePositive: "Falso positivo",
      needsRetest: "Precisa de reteste",
      runScan: "Rodar scan",
      latestFindings: "Últimos achados",
    },
    scan: {
      config: "Configuração do scan",
      portMode: "MODO DE SCAN DE PORTAS",
      phaseBudget: "ORÇAMENTO POR FASE",
      phaseBudgetHelp: "Tempo de relógio para cada fase do scan (recon passivo, descoberta de ativos, crawling, varredura de vulns, ...). Quando uma fase esgota, as ferramentas restantes nela são puladas e a próxima fase começa do zero.",
      requiresHigher: "Requer um plano superior ao {plan}",
      availableOn: "Disponível no {plan}",
      deepValidation: "⚔ Validação profunda (teste ativo)",
      dvEnabled: "Roda o SQLMap contra endpoints descobertos com query strings e formulários. Teste de injeção ativo — pode modificar ou expor dados no alvo. Só ative com autorização explícita por escrito do dono do ativo.",
      dvDisabled: "Desabilitado: este projeto não está pré-autorizado para validação profunda no nível do engagement. Contate seu pentester líder ou admin para habilitar engagement.deep_validation_authorized primeiro.",
      execute: "Executar pentest completo",
      dvArmed: "⚠ Validação profunda ARMADA",
      queued: "⚡ Scan na fila",
      queuedMsg: "Scan #{n} enfileirado com prioridade {priority}",
      queuedHint: "O worker vai pegá-lo em breve. O progresso aparecerá acima.",
      error: "Erro:",
      portModes: {
        common: "Comum (28 portas)",
        extended: "Estendido (1000+ portas)",
        full: "Completo (65535 portas)",
      },
      timeouts: {
        "300": "5 min (escopos rápidos)",
        "600": "10 min (equilibrado)",
        "1800": "30 min (minucioso)",
        "3600": "1 hora (escopos grandes)",
        "7200": "2 horas (enterprise)",
      },
    },
    phases: {
      passive_recon: "🔍 Reconhecimento passivo",
      asset_discovery: "🎯 Descoberta de ativos",
      fingerprinting: "🔬 Fingerprinting",
      crawling: "🕷 Crawling",
      vuln_scanning: "⚡ Varredura de vulnerabilidades",
      deep_validation: "⚔ Validação profunda",
    },
    liveProgress: {
      statusError: "Erro de status:",
      unknownError: "Erro desconhecido",
      connecting: "Conectando ao scan...",
      cancelTitle: "Cancelar este scan?",
      cancelMsg: "O progresso coletado até agora será preservado. O scan vai parar assim que a ferramenta atual terminar.",
      cancelConfirm: "Cancelar scan",
      cancelKeep: "Manter rodando",
      noActiveTask: "Nenhuma tarefa ativa encontrada",
      cancelled: "Scan cancelado",
      cancelFailed: "Falha ao cancelar:",
      inProgress: "Scan em andamento",
      elapsed: "Decorrido: {m}m {s}s",
      evidenceCollected: "Evidências coletadas",
      stepsCompleted: "Passos concluídos",
      statusLabel: "Status",
      nowRunning: "EXECUTANDO AGORA",
      phaseProgress: "Progresso das fases",
      stepsEvidence: "{completed}/{total} passos · {evidence} evidências",
      polling: "Atualizando a cada 3 segundos · Status preservado em disco",
      stopScan: "⏹ Parar scan",
    },
    evidence: {
      loading: "Carregando evidências...",
      searchPlaceholder: "Buscar evidências (título ou descrição)…",
      sortNewest: "Ordenar: Mais recentes",
      sortOldest: "Ordenar: Mais antigas",
      sortType: "Ordenar: Tipo",
      sortTitle: "Ordenar: Título",
      sortSeverity: "Ordenar: Severidade",
      all: "Todas ({n})",
      noMatch: "Nenhuma evidência corresponde aos filtros atuais.",
    },
    retest: {
      loading: "Carregando achados...",
      summary: "Resumo do reteste",
      toRetest: "Para retestar",
      fixedPass: "Corrigidos (Pass)",
      stillVuln: "Ainda vulnerável",
      noConfirmed: "Nenhum achado confirmado para retestar. Revise e confirme os achados primeiro.",
      retested: "retestado",
      notesLabel: "Notas do reteste",
      notesPlaceholder: "Testado novamente em...",
      markFixed: "✔ Corrigido (Pass)",
      markVuln: "✘ Ainda vulnerável",
      busy: "...",
      markedFixed: "Marcado como corrigido",
      markedVuln: "Marcado como ainda vulnerável",
      fixedCount: "Corrigidos ({n})",
      fixedBadge: "CORRIGIDO",
    },
    taskHistory: {
      error: "Erro:",
      loading: "Carregando histórico de tarefas...",
      none: "Nenhuma tarefa em segundo plano para este projeto ainda. Rode um scan para criar uma.",
      cancelTitle: "Cancelar esta tarefa?",
      cancelMsg: "O progresso coletado até agora é preservado. O scan para assim que a ferramenta atual terminar.",
      cancelConfirm: "Cancelar tarefa",
      cancelKeep: "Manter rodando",
      cancelled: "Tarefa cancelada",
      count: "{n} tarefa(s) · atualização automática a cada 5s",
      taskLabel: "Tarefa",
      prioritySuffix: "prioridade",
      retry: "tentativa {n}",
      cancelBtn: "⏹ Cancelar",
      running: "rodando...",
      progress: "PROGRESSO",
      phase: "FASE",
      evidence: "EVIDÊNCIAS",
      duration: "DURAÇÃO",
      created: "Criado:",
      done: "Concluído:",
      statuses: {
        queued: "na fila",
        running: "rodando",
        completed: "concluído",
        failed: "falhou",
        cancelled: "cancelado",
        retrying: "retentando",
        timeout: "timeout",
      },
      priorities: {
        high: "alta",
        normal: "normal",
        low: "baixa",
      },
    },
    findings: {
      loading: "Carregando achados...",
      all: "Todos ({n})",
      noMatch: "Nenhum achado corresponde a este filtro.",
      statuses: {
        needs_review: "Precisa de revisão",
        confirmed: "Confirmado",
        false_positive: "Falso positivo",
        needs_retest: "Precisa de reteste",
        retest_pass: "Reteste OK",
        retest_fail: "Reteste falhou",
        informational: "Informativo",
      },
    },
    findingDetail: {
      back: "← Voltar aos achados",
      aiEnriched: "Enriquecido por IA",
      descr: "Descrição",
      impact: "Impacto",
      remediation: "Remediação",
      riskAnalysis: "Análise de risco",
      level: "Nível:",
      priority: "Prioridade:",
      factors: "FATORES:",
      attackPlaybook: "⚔ Playbook de ataque",
      notApplicable: "Não aplicável para este achado",
      preconditions: "PRÉ-CONDIÇÕES:",
      attackChain: "CADEIA DE ATAQUE:",
      attackerGains: "O atacante ganha: ",
      detectionSignals: "SINAIS DE DETECÇÃO:",
      objective: "Objetivo: ",
      attackPath: "CAMINHO DE ATAQUE:",
      poc: "🔬 Prova de conceito",
      review: "Revisão",
      status: "STATUS",
      severityOverride: "SOBRESCREVER SEVERIDADE",
      reviewerNotes: "Notas do revisor",
      reviewerNotesPh: "Confirmado via teste manual...",
      pocLabel: "Prova de conceito",
      pocPh: "curl -v http://alvo/...",
      saving: "Salvando...",
      saveReview: "Salvar revisão",
      updated: "Achado atualizado",
      saveFailed: "Falha ao salvar:",
      ownershipToast: "Abra a aba Domínios neste projeto para verificar a propriedade do domínio.",
    },
    ownership: {
      loadingAssets: "Carregando ativos…",
      domainOwnership: "Propriedade do domínio",
      domainOwnershipDesc: "A análise de risco por IA e os playbooks de ataque rodam apenas em projetos cuja propriedade do domínio foi confirmada. Verifique cada domínio adicionando um registro DNS TXT OU um pequeno arquivo no webroot — qualquer um serve.",
      addDomain: "Adicionar domínio",
      addPlaceholder: "exemplo.com",
      adding: "Adicionando…",
      add: "Adicionar",
      willScan: "Vai escanear:",
      unknownError: "Erro desconhecido",
      verified: "✓ verificado",
      unverified: "não verificado",
      save: "Salvar",
      cancel: "Cancelar",
      destructiveWarn: "⚠ Destrutivo — isto vai apagar achados, evidências e histórico de scan deste projeto, e consumir sua troca de domínio única. Você verá uma confirmação antes de aplicar.",
      changing: "Alterando…",
      applyChange: "Aplicar alteração",
      checking: "Verificando…",
      verifyOwnership: "Verificar propriedade",
      readyToScan: "Pronto para escanear.",
      assetActions: "Ações do ativo",
      hideInstructions: "Ocultar instruções de verificação",
      showInstructions: "Mostrar instruções de verificação",
      editAsset: "Editar valor do ativo",
      changeDomainMenu: "Trocar domínio (uma vez)",
      domainChangeUsed: "Troca de domínio já usada para este projeto.",
      newDomainPh: "novo-dominio.com",
      changeTitle: "Trocar domínio?",
      changeMsg: "Substituir \"{old}\" por \"{new}\".\n\nIsto irá:\n  • Apagar todos os achados, evidências e histórico de scan deste projeto\n  • Resetar a verificação de propriedade (você precisará reverificar o novo domínio)\n  • Usar a sua única troca de domínio permitida para este projeto\n\nIsto não pode ser desfeito.",
      changeConfirm: "Trocar domínio",
      changeKeep: "Manter atual",
      method1: "Método 1 — registro DNS TXT",
      recordName: "Nome do registro",
      recordType: "Tipo do registro",
      recordValue: "Valor do registro",
      method2: "Método 2 — arquivo HTTP",
      url: "URL",
      fileContents: "Conteúdo do arquivo",
      instructionsHint: "Adicione qualquer um e clique em \"Verificar propriedade\". O servidor checa ambos; o que passar marca este ativo como verificado.",
    },
    report: {
      generating: "Gerando relatório...",
      client: "Cliente",
      generated: "Gerado",
      executiveSummary: "Resumo executivo",
      riskRating: "Classificação de risco",
      totalFindings: "Total de achados",
      evidenceCollected: "Evidências coletadas",
      assetsTested: "Ativos testados",
      severityBreakdown: "Distribuição por severidade",
      findingsDetail: "Detalhe dos achados",
    },
    profile: {
      page: {
        title: "Perfil e configurações",
        subtitle: "Gerencie sua conta e equipe",
        sectionAria: "Seção do perfil",
        tabs: {
          profile: "Perfil",
          security: "Senha e sessões",
          twofa: "Autenticação 2FA",
          members: "Membros",
          billing: "Cobrança",
        },
      },
      info: {
        anonymous: "Anônimo",
        auth: "Auth:",
        emailVerified: "● E-mail verificado",
        emailNotVerified: "● E-mail não verificado",
        currentOrg: "Organização atual",
        totalScans: "Total de scans",
        findings: "Achados",
        toolsExecuted: "Ferramentas executadas",
        evidence: "Evidências",
        allOrgs: "Todas as organizações",
      },
      security: {
        changePassword: "Alterar senha",
        currentPassword: "Senha atual",
        newPassword: "Nova senha",
        confirmNewPassword: "Confirmar nova senha",
        noMatch: "As novas senhas não coincidem",
        tooShort: "A senha deve ter pelo menos 8 caracteres",
        changed: "Senha alterada. As outras sessões foram revogadas.",
        busy: "...",
        changePasswordBtn: "Alterar senha",
        revokeSessionTitle: "Revogar esta sessão?",
        revokeSessionMsg: "O navegador/dispositivo conectado nesta sessão será desconectado na próxima vez que tentar usar o token atual.",
        revoke: "Revogar",
        sessionRevoked: "Sessão revogada",
        failed: "Falhou:",
        revokeAllTitle: "Revogar todas as sessões?",
        revokeAllMsg: "Todos os navegadores e dispositivos conectados serão desconectados — inclusive este. Você precisará entrar novamente aqui logo depois.",
        revokeAll: "Revogar todas",
        activeSessions: "Sessões ativas ({n})",
        revokeAllBtn: "Revogar todas",
        loading: "Carregando...",
        noSessions: "Nenhuma sessão ativa",
        unknown: "Desconhecido",
        createdLabel: "criada",
        lastUsedLabel: "último uso",
      },
      deleteAccount: {
        soleAdmin: "Você é o único admin de: {names}. Transfira os direitos de admin ou exclua essas organizações antes de fechar sua conta.",
        dangerZone: "⚠ Zona de perigo",
        deleteTitle: "Excluir sua conta",
        deleteDesc: "Fecha permanentemente sua conta. Seus dados pessoais (sessões, configurações de 2FA, associações) serão removidos. Projetos, achados e relatórios permanecem na sua organização, mas perdem a atribuição a você. Isto não pode ser desfeito.",
        deleteBtn: "Excluir conta",
        confirmTitle: "⚠ Confirmar exclusão da conta",
        confirmPre: "Esta ação é permanente. Digite ",
        confirmMid: "para confirmar, depois ",
        with2fa: "verifique com seu código 2FA",
        withPwd: "digite sua senha",
        typeToConfirm: "Digite \"DELETE\" para confirmar",
        deletePlaceholder: "DELETE",
        typeDeleteErr: "Digite DELETE para confirmar.",
        stepUpReason: "Digite seu código 2FA para excluir permanentemente sua conta",
        passwordRequired: "A senha é obrigatória.",
        currentPassword: "Senha atual",
        deleting: "Excluindo...",
        permanentlyDelete: "Excluir minha conta permanentemente",
        cancel: "Cancelar",
      },
    },
    twoFactor: {
      loading: "Carregando status do 2FA...",
      backupTitle: "✓ Autenticação de dois fatores ativada",
      backupDesc: "Salve estes códigos de backup agora. Eles NÃO serão mostrados novamente. Cada código pode ser usado uma vez caso você perca acesso ao app autenticador.",
      copyClipboard: "📋 Copiar",
      savedThem: "Já salvei",
      backupCopied: "Códigos de backup copiados",
      backupFileHeader: "Códigos de backup do 2FA — TrueHacking",
      backupFileFooter: "Guarde-os em um lugar seguro. Cada código pode ser usado apenas uma vez.",
      scanQr: "Escaneie este QR code com seu app autenticador",
      qrAlt: "QR Code do 2FA",
      enterManually: "Ou insira esta chave manualmente:",
      recommendedApps: "Apps recomendados: Google Authenticator, Authy, 1Password, Bitwarden",
      step2: "Passo 2 — Digite o código de 6 dígitos do seu app",
      verificationCode: "Código de verificação",
      codePlaceholder: "000000",
      busy: "...",
      activate: "Ativar 2FA",
      cancel: "Cancelar",
      disableTitle: "⚠ Desativar autenticação de dois fatores",
      disableDesc: "Isto removerá o 2FA da sua conta. Você não precisará mais de um código para entrar. Recomendamos manter o 2FA ativado — especialmente para contas de admin.",
      currentPassword: "Senha atual",
      currentCode: "Código 2FA atual (ou código de backup)",
      disableCodePlaceholder: "000000 ou XXXX-XXXX",
      disableBtn: "Desativar 2FA",
      requiredLabel: "Obrigatório:",
      requiredText: " Contas de superusuário precisam ativar a autenticação de dois fatores antes de acessar a plataforma. Configure o 2FA abaixo para continuar.",
      twoFactorAuth: "Autenticação de dois fatores",
      enabled: "Ativado",
      disabled: "Desativado",
      activatedOn: "Ativado em",
      backupRemaining: "{n} códigos de backup restantes",
      considerRegen: " — considere regenerar",
      disable2faBtn: "Desativar 2FA",
      enable2faBtn: "Ativar 2FA",
      recommendation: "⚠ Recomendação",
      recommendationText: "Ative o 2FA para proteger sua conta TrueHacking. Operações de admin no painel da plataforma exigirão verificação por 2FA. Escaneie um QR code com Google Authenticator, Authy, 1Password ou qualquer outro app TOTP.",
    },
    billing: {
      loading: "Carregando cobrança…",
      unavailable: "As informações de cobrança estão indisponíveis no momento.",
      portalError: "Não foi possível abrir o portal de cobrança.",
      currentPlan: "Plano atual",
      billingLabel: "Cobrança:",
      accessUntil: "Acesso até {date}",
      renews: "Renova em {date}",
      freeTier: "Você está no plano gratuito.",
      changePlan: "Mudar plano",
      upgrade: "Fazer upgrade",
      opening: "Abrindo…",
      manageBilling: "Gerenciar cobrança",
      portalNote: "Gerenciar cobrança abre seu portal seguro do Stripe — altere ou cancele seu plano, atualize seu cartão e baixe faturas. Os pagamentos são processados pelo Stripe; nunca vemos os dados do seu cartão.",
      statuses: {
        active: "Ativo",
        trialing: "Teste",
        past_due: "Em atraso",
        canceled: "Cancelado",
        unpaid: "Não pago",
        incomplete: "Incompleto",
      },
      cycles: {
        yearly: "Anual",
        monthly: "Mensal",
      },
    },
    members: {
      noOrg: "Nenhuma organização selecionada",
      roleUpdated: "Cargo atualizado",
      failed: "Falhou:",
      removeTitle: "Remover membro?",
      removeMsg: "{email} perderá acesso a esta organização imediatamente. A conta pessoal dele permanece — apenas a associação é revogada.",
      remove: "Remover",
      memberRemoved: "Membro removido",
      membersSuffix: "Membros",
      membersCount: "{n} membros",
      cancel: "Cancelar",
      inviteMember: "+ Convidar membro",
      inviteNew: "Convidar novo membro",
      email: "E-mail",
      emailPlaceholder: "usuario@empresa.com",
      fullNameOptional: "Nome completo (opcional)",
      role: "Cargo",
      tempPassword: "Senha temporária",
      tempPasswordPlaceholder: "Mín. 8 caracteres",
      busy: "...",
      createMember: "Criar membro",
      loading: "Carregando membros...",
      active: "● Ativo",
      disabled: "● Desativado",
      lastLogin: "Último login:",
      never: "Nunca",
      colUser: "Usuário",
      colStatus: "Status",
      colRole: "Cargo",
      colLastLogin: "Último login",
      roles: {
        admin: "Admin",
        pentester: "Pentester",
        viewer: "Visualizador",
        client: "Cliente",
      },
    },
    upgrade: {
      title: "Planos e cobrança",
      subtitle: "Você está no plano {plan}.",
      checkoutSuccess: "Assinatura atualizada — seu plano será refletido em breve.",
      checkoutCancelled: "Checkout cancelado — nenhuma cobrança foi feita.",
      checkoutError: "Não foi possível iniciar o checkout. Tente novamente ou contate o suporte.",
      portalError: "Não foi possível abrir o portal de cobrança.",
      billing: "Cobrança",
      monthly: "Mensal",
      annual: "Anual",
      currency: "Moeda",
      opening: "Abrindo…",
      manageBilling: "Gerenciar cobrança",
      loadingPlans: "Carregando planos…",
      current: "Atual",
      free: "Grátis",
      custom: "Sob consulta",
      perYr: " / ano",
      perMo: " / mês",
      twoMonthsFree: "2 meses grátis",
      unlimited: "Ilimitado",
      scansLabel: "scans / mês",
      engagementsLabel: "projetos",
      membersLabel: "membros da equipe",
      yourPlan: "Seu plano",
      contactSales: "Falar com vendas",
      choose: "Escolher {name}",
      starting: "Iniciando…",
      footnote: "A cobrança anual e os totais em USD (incluindo qualquer desconto) são exibidos com segurança na próxima etapa. Os pagamentos são processados pelo Stripe — nunca vemos os dados do seu cartão.",
      features: {
        ai_enrichment: "Análise de risco por IA e playbooks de ataque",
        deep_validation: "Validação profunda (exploração ativa)",
        ai_senior_pentester: "Nível de IA pentester sênior",
        sso: "Single sign-on (SSO)",
      },
    },
    scanHistory: {
      title: "Histórico de scans",
      subtitle: "Histórico de tarefas em segundo plano de todos os projetos",
      errorPrefix: "Erro:",
      unknownError: "Erro desconhecido",
      loading: "Carregando histórico de tarefas...",
      totalTasks: "Total de tarefas",
      activeNow: "Ativas agora",
      activeSub: "na fila + rodando",
      idleSub: "ocioso",
      completed: "Concluídas",
      failed: "Falharam",
      noTasks: "Nenhuma tarefa encontrada",
      colNum: "#",
      colEngagement: "Projeto",
      colStatus: "Status",
      colPriority: "Prioridade",
      colPhase: "Fase / Progresso",
      colResults: "Resultados",
      colDuration: "Duração",
      colWhen: "Quando",
      retry: "tentativa {n}",
      evShort: "ev",
      findShort: "achados",
      filters: {
        all: "Todas",
        running: "Rodando",
        queued: "Na fila",
        completed: "Concluídas",
        failed: "Falharam",
        cancelled: "Canceladas",
      },
      statuses: {
        queued: "na fila",
        running: "rodando",
        completed: "concluído",
        failed: "falhou",
        cancelled: "cancelado",
        retrying: "retentando",
        timeout: "timeout",
      },
      priorities: {
        high: "alta",
        normal: "normal",
        low: "baixa",
      },
      phases: {
        passive_recon: "Recon passivo",
        asset_discovery: "Descoberta de ativos",
        fingerprinting: "Fingerprinting",
        crawling: "Crawling",
        vuln_scanning: "Varredura de vulns",
        deep_validation: "Validação profunda",
        correlation: "Correlação",
        ai_enrichment: "Enriquecimento por IA",
        completed: "Concluído",
        failed: "Falhou",
        initializing: "Inicializando",
      },
      timeAgo: {
        seconds: "{n}s atrás",
        minutes: "{n}min atrás",
        hours: "{n}h atrás",
      },
    },
    adminPage: {
      platformAdminBadge: "ADMIN DA PLATAFORMA",
      stepUpActive: "● Step-up ativo",
      unresolvedOne: "⚠ {n} incidente não resolvido",
      unresolvedMany: "⚠ {n} incidentes não resolvidos",
      openHealth: "Abrir Saúde do Sistema",
      title: "Admin da plataforma",
      subtitle: "Operações de superusuário que afetam todos os usuários e organizações",
      warning: "⚠ Ações neste painel afetam TODOS os usuários e organizações da plataforma. Operações destrutivas exigem verificação 2FA step-up.",
      sectionAria: "Seção de admin",
      tabs: {
        dashboard: "Painel",
        users: "Usuários",
        orgs: "Organizações",
        tokens: "Tokens",
        settings: "Configurações",
        system: "Sistema",
        health: "Saúde",
        blog: "Blog",
      },
      gateSubtitle: "Operações de superusuário",
      gateTitle: "Autenticação de dois fatores obrigatória",
      gateBody: "O painel de admin da plataforma exige que o 2FA esteja ativado na sua conta. Isto é inegociável porque as operações de admin podem afetar todos os usuários e organizações da plataforma. Vá em Perfil → Autenticação 2FA para configurar.",
    },
    adminStepUp: {
      title: "Autenticação step-up",
      subtitle: "Reverifique o 2FA para operações de admin",
      codeLabel: "Código 2FA atual (ou código de backup)",
      codePlaceholder: "000000 ou XXXX-XXXX",
      busy: "...",
      verify: "Verificar",
      cancel: "Cancelar",
      tokenValid: "Token válido por 15 minutos após a verificação",
    },
    adminDashboard: {
      loading: "Carregando painel...",
      loadError: "Falha ao carregar o painel.",
      issuesHealth: "Problemas e saúde",
      unresolvedAlerts: "alertas de segurança não resolvidos",
      triageHint: "abra a aba Saúde para triar →",
      scansFailed: "scans falharam nas últimas 24h",
      failedLogins: "logins falhos nas últimas 24h",
      bruteForce: "possível força bruta",
      jobsBacklogged: "jobs acumulados (scans {scans}, enrich {enrich})",
      diskUsedOn: "de disco usado em {path}",
      allHealthy: "✓ Todos os sistemas saudáveis.",
      queueLabel: "Fila",
      asOf: "em",
      refresh: "↻ atualizar",
      sectionLive: "Ao vivo",
      scansToday: "scans hoje",
      scansInFlight: "scans em andamento",
      usersOnline15: "usuários online (15m)",
      sectionRevenue: "Receita e crescimento",
      mrr: "MRR",
      newSubsWk: "novas assinaturas (sem)",
      canceledWk: "canceladas (sem)",
      trials7d: "trials → 7d",
      planDistribution: "distribuição por plano",
      newOrgsWk: "novas orgs (sem)",
      newOrgsMo: "novas orgs (mês)",
      newUsersWk: "novos usuários (sem)",
      freeToPaid: "grátis → pago",
      sectionUsers: "Usuários",
      active30d: "ativos (30d)",
      mfaEnabled: "MFA ativado",
      onlineNow15: "online agora (15m)",
      roleDistribution: "distribuição por cargo",
      sectionAI: "IA",
      aiTriagedWk: "triados por IA (sem)",
      aiFpRate: "taxa de falso-positivo da IA",
      sectionTotals: "Totais",
      orgs: "orgs",
      users: "usuários",
      engagements: "projetos",
    },
    adminHealth: {
      intro: "Incidentes operacionais que a plataforma registrou automaticamente. Use o filtro de conta para suporte (\"cliente X relata um problema\"). Resolver um item apenas o reconhece — não corrige a causa raiz, mas limpa o alerta do painel.",
      accountSearch: "Buscar conta",
      accountPlaceholder: "e-mail ou nome da org",
      status: "Status",
      statusOpen: "Abertos",
      statusResolved: "Resolvidos",
      statusAll: "Todos",
      statusAria: "Filtro de status",
      severity: "Severidade",
      sevAny: "Qualquer",
      sevCritical: "Crítico",
      sevError: "Erro",
      sevWarning: "Aviso",
      severityAria: "Filtro de severidade",
      kind: "Tipo",
      kindAny: "Qualquer",
      kindScanTimeout: "Timeout de scan",
      kindEmailFailure: "Falha de e-mail",
      kindAiFailure: "Falha de IA",
      kindAria: "Filtro de tipo",
      loading: "Carregando incidentes...",
      noIncidents: "Nenhum incidente corresponde.",
      nothingOnFire: "Nada pegando fogo.",
      rangeOf: "de",
      prev: "Anterior",
      next: "Próximo",
      resolved: "✓ resolvido",
      resolve: "Resolver",
      incidentResolved: "Incidente resolvido",
      failed: "Falhou",
      hide: "ocultar detalhe",
      detail: "detalhe",
      whereToLook: "↳ Onde olhar:",
      kindLabels: {
        scan_timeout: "Timeout de scan",
        resend_failure: "Falha de e-mail (Resend)",
        ai_enrichment_failure: "Falha no enriquecimento por IA",
      },
      sevLabels: {
        critical: "Crítico",
        error: "Erro",
        warning: "Aviso",
      },
    },
    adminSystem: {
      refreshed: "{n} ferramentas atualizadas",
      refreshFailed: "Falha ao atualizar:",
      checking: "Verificando saúde das ferramentas...",
      overallHealth: "Saúde geral",
      toolsInstalled: "{installed}/{total} ferramentas instaladas",
      installed: "Instaladas",
      missing: "Faltando",
      categories: "Categorias",
      checkingBtn: "Verificando...",
      refreshBtn: "⟳ Atualizar",
      lastCheck: "Última verificação:",
      cachedFor: "em cache por",
      missingBadge: "FALTANDO",
      colTool: "Ferramenta",
      colVersion: "Versão",
      colDescError: "Descrição / Erro",
      colPath: "Caminho",
      unknown: "desconhecida",
      aboutTitle: "Sobre esta página:",
      aboutPre: "as verificações se baseiam em ",
      aboutPost: " para presença e uma chamada rápida de subprocesso para a versão (timeout de 3 segundos por ferramenta). Os resultados ficam em cache por 60 segundos. Esta página só verifica se os binários estão instalados e respondendo — não testa se funcionam contra alvos reais. Ferramentas faltando não são fatais — o orquestrador registra um aviso e as pula, mas sua cobertura sofre silenciosamente.",
      categoryLabels: {
        recon: "Recon passivo",
        discovery: "Descoberta de ativos",
        fingerprinting: "Fingerprinting",
        crawling: "Crawling",
        vuln_scanning: "Varredura de vulnerabilidades",
        deep_validation: "Validação profunda",
      },
    },
    adminSettings: {
      loading: "Carregando configurações...",
      modified: "● modificado",
      noChanges: "Sem alterações",
      settingsSaved: "Configurações salvas",
      saveFailed: "Falha ao salvar:",
      changesReverted: "Alterações revertidas",
      lastUpdated: "Última atualização:",
      never: "nunca",
      byUser: " pelo usuário #{id}",
      revert: "Reverter",
      save: "Salvar ({n} alterações)",
      secRegistration: "▸ Cadastro",
      regEnabled: "Cadastro habilitado",
      regEnabledDesc: "Quando desabilitado, novos usuários não podem se cadastrar via /auth/register. Os fluxos OAuth continuam funcionando. Usuários existentes não são afetados.",
      secOauth: "▸ Provedores OAuth",
      googleEnabled: "Google OAuth habilitado",
      googleDesc: "Permite que usuários entrem com o Google. Requer credenciais no .env (HYDRA_GOOGLE_OAUTH_CLIENT_ID/SECRET).",
      githubEnabled: "GitHub OAuth habilitado",
      githubDesc: "Permite que usuários entrem com o GitHub. Requer credenciais no .env.",
      secQuotas: "▸ Cotas",
      maxEng: "Máx. de projetos por organização",
      maxEngDesc: "Limite rígido de projetos em uma única org. Use 0 para ilimitado. Aplicado na criação.",
      unitEng: "proj",
      maxScans: "Máx. de scans por dia por usuário",
      maxScansDesc: "Cota diária (dia de calendário UTC) de execuções de scan por usuário. Use 0 para ilimitado. Superusuários ignoram.",
      unitScans: "scans",
      secAudit: "▸ Retenção de log de auditoria",
      auditRetention: "Retenção de auditoria (dias)",
      auditRetentionDesc: "Cron diário às 03:00 UTC remove entradas de auditoria mais antigas que isto. Piso rígido de 30 dias aplicado independentemente (LGPD).",
      unitDays: "dias",
      secAI: "▸ Enriquecimento por IA",
      aiEnabled: "Enriquecimento por IA habilitado",
      aiDesc: "Usa a TrueHacking AI para enriquecer achados com pontuação CVSS, playbooks de ataque e remediação. Desabilite se o serviço de IA estiver indisponível ou muito lento.",
      noteTitle: "Nota:",
      noteBody: "Algumas configurações (segredo JWT, origens CORS, HSTS) intencionalmente NÃO são editáveis aqui. Elas ficam no .env / docker-compose.yml e exigem reinício do contêiner para mudar. Isto é um design de segurança deliberado — a modificação em tempo de execução de limites de segurança via API é um alvo de alto valor para contas de admin comprometidas.",
    },
    adminTokens: {
      revokeUserTitle: "Revogar todas as sessões do usuário?",
      revokeUserMsg: "{email} será desconectado de todos os dispositivos na próxima requisição. A conta permanece — apenas as sessões ativas são encerradas.",
      revokeSessions: "Revogar sessões",
      revokedFor: "{n} sessões revogadas para {email}",
      revokeFailed: "Falha ao revogar:",
      mustType: "Você precisa digitar 'REVOKE ALL' exatamente",
      nuclearDone: "☢ {n} sessões REVOGADAS globalmente — desconectando você",
      nuclearFailed: "Falha na revogação nuclear:",
      totalTokens: "Total de tokens",
      active: "Ativos",
      revoked: "Revogados",
      usersWithSessions: "Usuários c/ sessões",
      filterPlaceholder: "Filtrar por user_id (número)...",
      includeRevoked: "Incluir revogados",
      nuclearBtn: "☢ Nuclear: Revogar TODOS",
      nuclearTitle: "☢ OPÇÃO NUCLEAR",
      nuclearDesc: "Isto revogará TODOS os refresh tokens ativos da plataforma. Todos os usuários — inclusive você — serão desconectados imediatamente e precisarão entrar de novo. Use apenas em resposta a um incidente de segurança confirmado (suspeita de comprometimento do segredo JWT, vazamento de credenciais, etc).",
      reasonLabel: "Motivo (registrado no log de auditoria)",
      reasonPlaceholder: "ex.: Suspeita de comprometimento do segredo JWT em 2026-04-08",
      typeToConfirm: "Digite REVOKE ALL para confirmar:",
      execute: "☢ Executar",
      cancel: "Cancelar",
      loading: "Carregando tokens...",
      noTokens: "Nenhum token corresponde aos filtros atuais",
      statusRevoked: "● Revogado",
      statusActive: "● Ativo",
      uaPrefix: "UA:",
      ipPrefix: "IP:",
      createdPrefix: "Criado:",
      revokeUser: "Revogar usuário",
      idPrefix: "id",
      colUser: "Usuário",
      colUA: "User Agent",
      colIP: "IP",
      colStatus: "Status",
      colCreated: "Criado",
      prev: "← Anterior",
      next: "Próximo →",
      rangeOf: "de",
    },
    adminOrgs: {
      orgCreated: "Organização criada",
      createFailed: "Falha ao criar:",
      forceDeleteTitle: "Forçar exclusão da organização?",
      deleteTitle: "Excluir organização?",
      forceDeleteMsg: "FORÇAR EXCLUSÃO de {name}, incluindo {n} projetos + todos os achados, evidências e histórico de scan. Os membros perdem acesso imediatamente. Isto não pode ser desfeito.",
      deleteMsg: "Excluir {name}. Falha se a org tiver projetos ativos — use Forçar exclusão nesse caso.",
      forceDelete: "Forçar exclusão",
      delete: "Excluir",
      orgDeleted: "Organização excluída",
      deleteFailed: "Falha ao excluir:",
      searchPlaceholder: "Buscar nome ou slug...",
      orgsCount: "{n} orgs",
      cancel: "Cancelar",
      newOrg: "+ Nova org",
      createTitle: "Criar nova organização",
      nameLabel: "Nome",
      namePlaceholder: "Acme Corp",
      descLabel: "Descrição (opcional)",
      createBtn: "Criar organização",
      loading: "Carregando orgs...",
      noOrgs: "Nenhuma organização encontrada",
      membersLabel: "membros",
      engagementsLabel: "projetos",
      colName: "Nome",
      colUUID: "UUID",
      colPlan: "Plano",
      colMembers: "Membros",
      colEngagements: "Projetos",
      detail: "Detalhe →",
      prev: "← Anterior",
      next: "Próximo →",
      rangeOf: "de",
      membersStat: "Membros",
      engagementsStat: "Projetos",
      byStatus: "Por status",
      membersCount: "Membros ({n})",
      actionsStepUp: "Ações (step-up obrigatório)",
      deleteSafe: "Excluir (seguro)",
      forceDeleteCascade: "🗑 Forçar exclusão (cascata)",
    },
    adminUsers: {
      disableTitle: "Desabilitar usuário?",
      enableTitle: "Habilitar usuário?",
      disableMsg: "{email} perde acesso à plataforma imediatamente. As sessões continuam válidas até a próxima requisição, então caem para o login.",
      enableMsg: "{email} recupera acesso à plataforma imediatamente.",
      disable: "Desabilitar",
      enable: "Habilitar",
      userDisabled: "Usuário desabilitado",
      userEnabled: "Usuário habilitado",
      failed: "Falhou:",
      resetTitle: "Resetar senha?",
      resetMsg: "{email} recebe uma senha recém-gerada e TODAS as suas sessões são revogadas. Precisará entrar de novo com a nova senha — que será mostrada uma vez e copiada para a área de transferência.",
      resetConfirm: "Resetar senha",
      newPwdTitle: "Nova senha gerada",
      newPwdMsg: "Copiada para a área de transferência:\n\n{pwd}\n\nEsta é a única vez que será exibida. {n} sessões revogadas.",
      savedIt: "Salvei",
      pwdReset: "Senha resetada — {n} sessões revogadas",
      resetFailed: "Falha ao resetar:",
      grantTitle: "Conceder superusuário?",
      revokeTitle: "Revogar superusuário?",
      grantMsg: "{email} GANHARÁ acesso de admin da plataforma — poderá realizar operações destrutivas em todos os usuários e organizações.",
      revokeMsg: "{email} perderá o acesso de admin da plataforma — não alcançará mais o painel de admin.",
      grantConfirm: "Conceder superusuário",
      revokeConfirm: "Revogar superusuário",
      superUpdated: "Flag de superusuário atualizada",
      updateFailed: "Falha ao atualizar:",
      markVerifiedTitle: "Marcar e-mail como verificado?",
      markVerifiedMsg: "Marca manualmente email_verified=true para {email}. Use apenas para casos de suporte (erro de digitação no cadastro, perda de acesso à caixa de entrada, conta pré-gate). A ação é registrada no log de auditoria.",
      markVerifiedConfirm: "Marcar verificado",
      emailMarkedVerified: "E-mail marcado como verificado",
      markVerifiedFailed: "Falha ao marcar verificado:",
      exported: "Dados do usuário exportados (LGPD)",
      exportFailed: "Falha ao exportar:",
      anonTitle: "Anonimizar usuário (apagamento LGPD)?",
      anonMsg: "Direito ao apagamento para {email}. Isto irá:\n\n• Substituir o e-mail por deleted-user-{id}@anonymized.local\n• Apagar nome, avatar, credenciais\n• Revogar todas as sessões\n• Remover de todas as organizações\n\nIsto é irreversível. O log de auditoria registrará quem executou e quando.",
      anonConfirm: "Anonimizar",
      userAnonymized: "Usuário anonimizado",
      anonFailed: "Falha ao anonimizar:",
      searchPlaceholder: "Buscar e-mail ou nome...",
      allStatus: "Todos os status",
      active: "Ativos",
      disabled: "Desabilitados",
      allRoles: "Todos os papéis",
      superusers: "Superusuários",
      regular: "Comuns",
      all2fa: "Todos 2FA",
      twofaEnabledOpt: "2FA ativado",
      twofaDisabledOpt: "2FA desativado",
      usersCount: "{n} usuários",
      exportCsv: "Exportar CSV",
      exportOptions: "Opções do export",
      applyScreenFilters: "Aplicar filtros da tela",
      includeDisabled: "Incluir desabilitados",
      includeUnverified: "Incluir e-mail não verificado",
      exportAlwaysExcludes: "Superusuários e contas anonimizadas nunca são exportados.",
      downloadCsv: "Baixar CSV",
      exportingCsv: "Exportando...",
      csvExported: "CSV de usuários baixado",
      csvExportFailed: "Falha ao exportar CSV:",
      loading: "Carregando usuários...",
      noUsers: "Nenhum usuário corresponde aos filtros atuais",
      super: "SUPER",
      statusAnonymized: "● Anonimizado",
      statusActive: "● Ativo",
      statusDisabled: "● Desabilitado",
      emailConfirmed: "E-mail confirmado",
      emailNotVerified: "E-mail ainda não verificado",
      verified: "verificado",
      unverified: "não verificado",
      twofaOn: "ligado",
      twofaOff: "desligado",
      lastLogin: "Último login:",
      never: "Nunca",
      colUser: "Usuário",
      colStatus: "Status",
      colRole: "Papel",
      colVerified: "Verificado",
      col2fa: "2FA",
      colLastLogin: "Último login",
      anon: "● Anon",
      emailConfirmedDesk: "E-mail confirmado pelo usuário",
      emailNotVerifiedDesk: "O usuário ainda não clicou no link de verificação",
      yes: "✓ Sim",
      no: "✗ Não",
      onLabel: "✓ Ligado",
      offLabel: "Desligado",
      detail: "Detalhe →",
      prev: "← Anterior",
      next: "Próximo →",
      rangeOf: "de",
      userNum: "Usuário #{id}",
      statusLabel: "Status",
      superuser: "Superusuário",
      yesShort: "Sim",
      noShort: "Não",
      twofa: "2FA",
      enabledShort: "Ativado",
      disabledShort: "Desativado",
      authProvider: "Provedor de auth",
      activeSessions: "Sessões ativas",
      engagementsCreated: "Projetos criados",
      lgpdData: "Dados LGPD",
      consent: "Consentimento:",
      consentVer: "(v{v})",
      preLgpd: "Conta pré-LGPD",
      anonymizedAt: "Anonimizado:",
      organizations: "Organizações",
      anonNoActions: "Este usuário está anonimizado. Nenhuma ação adicional disponível.",
      actionsStepUp: "Ações (step-up obrigatório)",
      disableUser: "Desabilitar usuário",
      enableUser: "Habilitar usuário",
      moreActions: "Mais ações do usuário",
      resetPassword: "Resetar senha",
      revokeSuperuser: "Revogar superusuário",
      grantSuperuser: "Conceder superusuário",
      markEmailVerified: "Marcar e-mail como verificado",
      exportData: "Exportar dados (LGPD)",
      anonymizeErasure: "Anonimizar (apagamento LGPD)",
    },
    adminBlog: {
      newPost: "Novo artigo",
      editPost: "Editar artigo",
      noPosts: "Nenhum artigo ainda.",
      loading: "Carregando...",
      colTitle: "Título",
      colLocale: "Idioma",
      colStatus: "Status",
      colUpdated: "Atualizado",
      draft: "Rascunho",
      published: "Publicado",
      edit: "Editar",
      delete: "Excluir",
      deleteTitle: "Excluir artigo?",
      deleteMsg: "\"{title}\" será excluído permanentemente.",
      deleteConfirm: "Excluir",
      deleted: "Artigo excluído",
      saved: "Artigo salvo",
      saveFailed: "Falha ao salvar:",
      fldTitle: "Título",
      fldSlug: "Slug (opcional)",
      slugPlaceholder: "gerado do título",
      fldLocale: "Idioma",
      fldExcerpt: "Resumo (opcional)",
      excerptPlaceholder: "Resumo curto para os cards e SEO",
      fldStatus: "Status",
      fldCover: "Imagem de capa",
      uploadCover: "Enviar imagem",
      uploading: "Enviando...",
      removeCover: "Remover",
      uploadFailed: "Falha no upload",
      fldBody: "Conteúdo",
      cancel: "Cancelar",
      save: "Salvar",
      saving: "Salvando...",
      stepUpSave: "Confirme seu 2FA para salvar o artigo",
      stepUpDelete: "Confirme seu 2FA para excluir o artigo",
      stepUpUpload: "Confirme seu 2FA para enviar a imagem",
    },
    ui: {
      confirmDelete: "Excluir",
      confirm: "Confirmar",
      cancel: "Cancelar",
      dismiss: "Dispensar",
      teaserPreparing: "A análise de IA está sendo preparada. Isto normalmente leva alguns minutos após o scan terminar (ou após a verificação da propriedade do domínio).",
      teaserRefresh: "Atualizar",
      verifyOwnership: "Verificar propriedade do domínio",
      upgradeUnlock: "Fazer upgrade para desbloquear",
      preview: "(prévia)",
      premiumFeature: "🔒 Recurso premium",
      upgradeTitle: "Faça upgrade do seu plano",
      upgradeBody: "Este recurso está disponível nos planos pagos. Os pagamentos ainda não estão ativos — ainda estamos ajustando o fluxo de cobrança.",
      upgradeContact: "Quer acesso antecipado? Entre em contato:",
      gotIt: "Entendi",
    },
  },
};

export type Dictionary = typeof pt;

const en: Dictionary = {
  nav: {
    pricing: "Pricing",
    platform: "Platform",
    contact: "Contact",
    blog: "Blog",
    login: "Log in",
    startFree: "Start free",
    goToApp: "Go to app →",
  },
  blog: {
    meta: {
      title: "Blog — TrueHacking",
      description: "Articles on offensive security, pentesting and attack surface management.",
    },
    eyebrow: "BLOG",
    title: "Blog",
    subtitle: "Offensive security, pentesting and attack surface management — straight from the team building TrueHacking.",
    readMore: "Read more →",
    empty: "No articles published yet.",
    loading: "Loading…",
    backToBlog: "← Back to blog",
    notFound: "Article not found.",
  },
  footer: {
    blurb:
      "AI-assisted offensive security for teams that ship fast and need to stay ahead of attackers.",
    product: "Product",
    company: "Company",
    pricing: "Pricing",
    privacy: "Privacy Policy",
    terms: "Terms of Service",
    rights: "All rights reserved.",
  },
  landing: {
    meta: {
      title:
        "TrueHacking — AI Penetration Testing & Attack Surface Management",
      description:
        "TrueHacking is an AI pentest copilot: continuous reconnaissance, external attack surface management, CVSS-scored findings, and realistic attack playbooks — without waiting weeks for a pentest report.",
    },
    hero: {
      eyebrow: "AI PENTEST COPILOT",
      tagline: "Your AI Pentester. Hacking your SaaS risks before hackers do.",
      subline:
        "AI offensive security that finds what scanners miss. Continuous reconnaissance, intelligent vulnerability analysis, External Attack Surface Management, and attack playbooks for true penetration testing — without waiting weeks for a report.",
      ctaPrimary: "Start for free",
      ctaPrimaryAuthed: "Open dashboard →",
      seePricing: "See pricing",
      socialProof:
        "Trusted by security teams across finance, SaaS, SMBs, and e-commerce.",
    },
    problem: {
      eyebrow: "THE PROBLEM",
      title: "You can't defend what you can't see.",
      points: [
        "You don't know how many of your assets are actually exposed to the internet.",
        "Traditional pentests are expensive, take weeks, and are stale the moment they're delivered.",
        "Your security tools drown you in low-priority findings without telling you what to fix first.",
        "Credentials, config files, and internal services leak through forgotten subdomains you never see.",
        "You pay for WAFs, EDRs, and SOCs but still can't answer 'are we actually secure?'",
      ],
    },
    platform: {
      eyebrow: "THE PLATFORM",
      title: "One tool to cover your entire attack surface",
      subtitle:
        "TrueHacking automates what a senior pentester would do manually, then layers AI on top to prioritize and explain every finding.",
      features: [
        {
          title: "Continuous reconnaissance",
          body: "Map your external attack surface and monitor it 24/7. Subdomains, ports, services, and forgotten assets — discovered and tracked automatically.",
        },
        {
          title: "AI-powered analysis",
          body: "Every finding is enriched with CVSS scoring, contextual risk factors, and a realistic attack playbook. Skip the false-positive noise.",
        },
        {
          title: "Exploit path simulation",
          body: "See exactly how an attacker would chain vulnerabilities end-to-end. Prioritize what actually matters, not just what's severe on paper.",
        },
        {
          title: "One-click reports",
          body: "Executive summary, technical detail, proof-of-concept commands. Ready for auditors, clients, and your board — in one export.",
        },
      ],
    },
    how: {
      eyebrow: "HOW IT WORKS",
      title: "Three steps from zero to actionable findings",
      steps: [
        {
          title: "Create an engagement",
          body: "Point TrueHacking at a domain, IP range, or cloud tenant. Pick a scan profile — web, API, network, cloud, mobile, IoT, or AI/ML systems.",
        },
        {
          title: "Run the scan",
          body: "Seven automated phases: passive recon, asset discovery, fingerprinting, crawling, vulnerability scanning, deep validation. Live progress while it runs.",
        },
        {
          title: "Review and act",
          body: "Findings are correlated, scored, and prioritized. Human review confirms the critical ones. Export a report when you're done.",
        },
      ],
    },
    ai: {
      eyebrow: "TRUEHACKING AI",
      title: "A senior pentester's brain, on every finding",
      body: "Every vulnerability is analyzed contextually: CVSS v3.1 scoring with justification, an attack playbook with realistic exploit steps, reproduction commands, and remediation tailored to your stack. No generic copy-paste. No false-positive spam. Just the three things a pentester would tell you if they had all day.",
    },
    pricing: {
      eyebrow: "PRICING",
      title: "Start free. Upgrade when you need more.",
      subtitle:
        "Free tier gets you started without a credit card. No per-seat surprises — pricing scales with scans and features.",
      loading: "Loading plans…",
      perMonth: "per month",
      custom: "Custom",
      mostPopular: "Most popular",
      engagements: "{n} engagements",
      scans: "{n} scans / month",
      users: "{n} users",
      unlimited: "Unlimited",
      ctaFree: "Start free",
      ctaEnterprise: "Contact sales",
      ctaGet: "Get {name}",
      monthly: "Monthly",
      annual: "Annual",
      save: "2 months free",
      perYear: "per year",
    },
    featureLabels: {
      ai_enrichment: "AI-powered risk analysis",
      ai_senior_pentester: "AI Senior Pentester",
      deep_validation: "Deep validation (SQLMap, active tests)",
      support_low: "Low priority support",
      support_5x8: "Support 5x8",
      support_high: "High priority support",
      sso: "Single sign-on (SSO)",
    },
    finalCta: {
      title: "Ready to see your real attack surface?",
      body: "Create an account in thirty seconds. Run your first scan in five minutes. Start for free — no credit card, no call with a salesperson.",
      button: "Start for free →",
      buttonAuthed: "Open dashboard →",
    },
  },

  platform: {
    meta: {
      title: "Platform — TrueHacking",
      description: "From recon to report: surface discovery, leaked exposures, authorized active validation, correlation, AI-explained findings, and client-ready reports.",
    },
    hero: {
      eyebrow: "THE PLATFORM",
      title: "An offensive security platform, from recon to report.",
      subline: "We bring the best offensive arsenal together and layer AI on top — to discover, validate, and explain real risk, with you in control of every active test.",
    },
    capabilities: {
      eyebrow: "CAPABILITIES",
      title: "Everything you need for a complete engagement.",
      items: [
        { title: "Map your entire attack surface", body: "Before touching anything, the platform discovers what's exposed: subdomains, DNS, open ports, technologies in use, and WAF presence." },
        { title: "Find what's already leaked", body: "Forgotten secrets in repos, emails in known breaches, open buckets, and typosquatting — everything an attacker would find first." },
        { title: "Validate for real — only when you authorize", body: "Active tests (like SQLMap) run only within the engagement scope and only after your explicit authorization." },
        { title: "Many tools, one source of truth", body: "The whole arsenal feeds a correlation engine that dedupes and connects results into a single, noise-free view." },
        { title: "Findings that explain themselves", body: "AI rewrites every finding with description, impact, and remediation. The AI Senior Pentester validates and guides confirmation, at a senior consultant's level." },
        { title: "Executive report, ready to deliver", body: "Risk rating, severity breakdown, evidence, and technical appendix — formatted and exportable. From finding to client PDF with no rework." },
        { title: "Many clients, isolated accounts", body: "Separate orgs and workspaces, per-user roles, and an admin panel. SSO on Enterprise. Each client in its own box, no cross-account leakage." },
      ],
    },
    workflow: {
      eyebrow: "THE FLOW",
      title: "How an engagement happens",
      steps: [
        "Define the scope and authorized targets.",
        "The surface is mapped and exposures surfaced.",
        "Active tests only after your authorization.",
        "Each result becomes a finding with impact and remediation.",
        "You confirm what goes into the report.",
        "Export as PDF, ready for the client.",
      ],
    },
    control: {
      eyebrow: "CONTROL",
      title: "Offensive power, always under your command.",
      items: [
        { title: "Explicit authorization", body: "Active tests only run when you authorize them, per engagement." },
        { title: "Human-in-the-loop", body: "Nothing enters the report without your review and approval." },
        { title: "Per-account isolation", body: "Isolated orgs and workspaces, with roles and an admin panel." },
      ],
    },
    cta: {
      eyebrow: "START NOW",
      title: "See the platform in action.",
      body: "Start on the free plan or book a demo.",
      button: "Start for free →",
    },
  },

  pricing: {
    meta: { title: "Pricing — TrueHacking", description: "Pricing that scales with you, not your headcount. Start free, no card. Pay for scans and features, not per seat." },
    hero: { eyebrow: "PRICING", title: "Pricing that scales with you, not your team.", lead: "Start for free, no card. No per-seat surprises — you pay for scans and features, not per person." },
    comparison: {
      eyebrow: "COMPARISON",
      title: "Compare the plans in detail",
      groupLimits: "Limits",
      groupFeatures: "Features",
      engagements: "Engagements",
      scans: "Scans per month",
      users: "Users",
    },
    cta: { eyebrow: "START NOW", title: "Start free today. Upgrade when you need to.", body: "No credit card. No per-seat surprises.", buttonFree: "Start for free", buttonSales: "Contact sales" },
  },

  app: {
    nav: {
      dashboard: "Dashboard",
      engagements: "Engagements",
      history: "Scan History",
      profile: "Profile & Usage",
      upgrade: "Upgrade Plan",
      admin: "Platform Admin",
    },
    adminBadge: "ADMIN",
    logout: "Logout",
    closeMenu: "Close menu",
    tagline: "Think like many.\nAttack as one.",
    aiStatus: "TrueHacking AI",
    services: "Services",
    auth: {
      copilot: "AI Pentest Copilot",
      tagline: "Think like many. Attack as one.",
      signIn: "Sign in",
      createAccount: "Create account",
      signInSub: "Access your pentest workspace",
      registerSub: "Start your first workspace",
      twofaTitle: "Two-factor verification",
      twofaHeading: "Enter 6-digit code",
      twofaHint: "Open your authenticator app and enter the code. You can also use a backup code.",
      codeLabel: "Code",
      codePlaceholder: "000000 or XXXX-XXXX",
      verify: "Verify",
      busy: "...",
      backToSignin: "Back to sign in",
      google: "Continue with Google",
      github: "Continue with GitHub",
      or: "or",
      email: "Email",
      emailPlaceholder: "you@company.com",
      password: "Password",
      passwordPlaceholder: "••••••••",
      fullName: "Full name",
      fullNamePlaceholder: "John Doe",
      org: "Organization name",
      orgPlaceholder: "Acme Security",
      consentPre: "I agree to the",
      terms: "Terms of Service",
      consentAnd: "and",
      privacy: "Privacy Policy",
      consentPost: ", and I consent to the processing of my personal data as described. (LGPD Art. 7º, I)",
      needAccount: "Need an account? Register",
      haveAccount: "Already have an account? Sign in",
      forgot: "Forgot password?",
      noOauth: "OAuth providers not configured",
      recoverySubtitle: "Password recovery",
      checkEmail: "Check your email",
      forgotSentPre: "If an account with",
      forgotSentPost: " exists, we've sent a password reset link. The link expires in 15 minutes.",
      forgotHeading: "Forgot your password?",
      forgotSub: "Enter your email and we'll send you a link to reset it.",
      sendReset: "Send reset link",
      resetSubtitle: "Set new password",
      missingToken: "Missing reset token. Redirecting to sign-in...",
      pwdUpdatedTitle: "Password updated",
      pwdUpdatedBody: "Your password has been changed. All active sessions were signed out. Redirecting to sign in...",
      signInNow: "Sign in now →",
      chooseNewPwd: "Choose a new password",
      chooseNewPwdSub: "At least 8 characters. Pick something memorable.",
      newPassword: "New password",
      confirmPassword: "Confirm password",
      updatePassword: "Update password",
      errPwdShort: "Password must be at least 8 characters.",
      errPwdMismatch: "Passwords don't match.",
      verifySubtitle: "Email verification",
      verifying: "Verifying your email...",
      verifiedTitle: "✓ Email verified",
      verifiedConfirmed: "is now confirmed.",
      verifiedSignedIn: " You're signed in already — continue to your dashboard.",
      verifiedSignIn: " Sign in to continue.",
      goDashboard: "Go to dashboard →",
      continueSignIn: "Continue to sign in",
      linkInvalidTitle: "Link invalid or expired",
      noTokenMsg: "No verification token in the link. Open the link from your email again.",
      invalidMsg: "This verification link is invalid or has expired. Request a new one.",
      requestNewLink: "Request a new link",
    },
    openMenu: "Open menu",
    errors: {
      notFoundCode: "ERROR 404 · NOT FOUND",
      notFoundTitle: "We couldn't find that page.",
      notFoundMsg: "The link may be broken, or the page may have been moved. Check the URL or head back to a known starting point.",
      goHome: "Go to home",
      openDashboard: "Open dashboard",
      unavailableCode: "ERROR 503 · SERVICE UNAVAILABLE",
      unavailableTitle: "We're having trouble reaching our servers.",
      unavailableMsg: "The platform is temporarily unavailable — we may be deploying an update or recovering from an incident. We're checking every 5 seconds and will send you back as soon as it's up.",
      tryNow: "Try now",
      backDashboard: "Back to dashboard",
      backHome: "Back to home",
      errorCode: "ERROR 500 · UNEXPECTED ERROR",
      errorTitle: "Something went wrong.",
      errorMsg: "We hit an unexpected problem rendering this page. Reloading often clears it; if not, try heading back home and opening it again.",
      reload: "Reload page",
    },
    verifyRequired: {
      vrTitle: "Verify your email",
      oneMoreStep: "One more step",
      sentPre: "We sent a verification link to",
      sentPost: ". Click it to finish setting up your account, then come back to sign in. The link is valid for 3 days.",
      unsentPre: "To finish setting up your account, click below to send a verification link to",
      unsentPost: ". The link will be valid for 3 days.",
      sending: "Sending...",
      resendIn: "Resend in {t}",
      resendEmail: "Resend verification email",
      sendEmail: "Send verification email",
      deliveryFailedPre: "We couldn't deliver the email right now. Try again in a few minutes — if it keeps failing, contact",
      deliveryFailedPost: ".",
      coldBody: "We don't know which account you're trying to verify on this device. Sign in with the credentials you registered with — if your email isn't verified yet, you'll be sent back here automatically with a Resend button.",
      goSignIn: "Go to sign in",
      signOutOther: "Sign out and use a different account",
      backToSignin: "← Back to sign in",
      hintPre: "Didn't get it? Check your spam folder. The link is sent from",
      hintMid: ". If you mistyped your email at signup, contact",
      hintPost: ".",
      tooSoon: "Too soon — try again in {t}.",
      cantSend: "Couldn't send right now. Try again in a few minutes — if it keeps failing, contact support.",
      freshLink: "Fresh link is on the way.",
      couldNotRequest: "Could not request a new link.",
    },
    dashboard: {
      loading: "Loading dashboard...",
      error: "Error:",
      unknownError: "Unknown error",
      title: "Dashboard",
      subtitle: "Real-time overview of your TrueHacking platform",
      plan: "Plan",
      scansThisMonth: "Scans this month",
      usageEngagements: "Engagements",
      totalEngagements: "Total Engagements",
      thisWeek: "+{n} this week",
      totalFindings: "Total Findings",
      criticalPending: "{n} critical pending",
      evidenceCollected: "Evidence Collected",
      avgScanDuration: "Avg Scan Duration",
      successRate: "{n}% success rate",
      currentlyScanning: "Currently Scanning ({n})",
      noScans: "No scans in progress",
      started: "started",
      recently: "recently",
      live: "LIVE",
      serviceHealth: "Service Health",
      findingsBySeverity: "Findings by Severity",
      pentestTypes: "Pentest Types",
      noData: "No data yet",
      findingsTrend: "Findings Trend (last 14 days)",
      criticalFindings: "⚠ Critical Findings (pending review)",
      noneGreat: "None — great!",
      recentEngagements: "Recent Engagements",
      noEngagements: "No engagements yet",
      topTools: "Top Tools (most used)",
      runs: "{n} runs",
      executionStats: "Execution Stats",
      totalExecutions: "Total executions",
      successful: "Successful",
      errorsTimeouts: "Errors / Timeouts",
      successRateLabel: "Success rate",
      errorRate: "Error rate",
    },
    engagements: {
      loading: "Loading engagements...",
      error: "Error:",
      unknownError: "Unknown error",
      title: "Engagements",
      subtitle: "{total} total · {running} running (loaded)",
      planLimit: "Plan limit reached ({used}/{max}). Upgrade or archive one.",
      planLimitReached: "🔒 Plan limit reached",
      newEngagement: "+ New Engagement",
      searchPlaceholder: "Search by name, client, or target…",
      sortNewest: "Sort: Newest first",
      sortOldest: "Sort: Oldest first",
      sortName: "Sort: Name",
      sortStatus: "Sort: Status",
      statusLabel: "Status",
      companyAll: "Company: All",
      noMatch: "No engagements match the current filters.",
      noClient: "No client",
      showing: "Showing {n} of {total}",
      loadMore: "Load more",
      loadingMore: "Loading…",
      statuses: {
        all: "all",
        draft: "draft",
        active: "active",
        scanning: "scanning",
        review: "review",
        retesting: "retesting",
        completed: "completed",
        archived: "archived",
      },
    },
    newEngagement: {
      pickTitle: "What type of pentest?",
      pickSubtitle: "This determines which tools and scanning strategies TrueHacking will use",
      bcEngagements: "Engagements",
      bcNew: "New",
      detailsTitle: "{label} Pentest",
      detailsSubtitle: "Configure engagement details and targets",
      changeType: "← Change type",
      nameLabel: "Engagement name",
      namePlaceholder: "Q2 2026 External Pentest",
      clientLabel: "Client name",
      clientPlaceholder: "Acme Corp",
      scopeLabel: "Scope description",
      scopePlaceholder: "External web applications and APIs",
      targetsLabel: "Targets (one per line)",
      error: "Error:",
      unknown: "Unknown",
      creating: "Creating...",
      createContinue: "Create & continue",
      types: {
        web_application: { label: "Web Application", description: "Full web app pentest — XSS, SQLi, auth bypass" },
        api: { label: "API Security", description: "REST/GraphQL — auth, injection, rate limiting" },
        network: { label: "Network", description: "Port scanning, service enumeration, known CVEs" },
        cloud: { label: "Cloud", description: "S3 buckets, exposed services, misconfigurations" },
        mobile: { label: "Mobile", description: "Mobile app backend — API endpoints, auth" },
        iot: { label: "IoT", description: "Default creds, exposed services, firmware" },
        ai: { label: "AI/ML", description: "Model endpoints, data exposure, prompt injection" },
      },
    },
    engagementDetail: {
      loading: "Loading engagement...",
      noClient: "No client",
      created: "Created",
      bcEngagements: "Engagements",
      sectionAria: "Engagement section",
      tabs: {
        overview: "Overview",
        scanning: "⚡ Scanning...",
        scan: "Scan",
        domains: "Domains",
        findings: "Findings ({n})",
        evidence: "Evidence",
        retest: "Retest",
        history: "Task History",
        report: "Report",
      },
    },
    severities: {
      critical: "Critical",
      high: "High",
      medium: "Medium",
      low: "Low",
      informational: "Info",
    },
    overview: {
      loading: "Loading overview...",
      assets: "Assets ({n})",
      reviewProgress: "Review progress",
      needsReview: "Needs review",
      confirmed: "Confirmed",
      falsePositive: "False positive",
      needsRetest: "Needs retest",
      runScan: "Run scan",
      latestFindings: "Latest findings",
    },
    scan: {
      config: "Scan configuration",
      portMode: "PORT SCAN MODE",
      phaseBudget: "PHASE BUDGET",
      phaseBudgetHelp: "Wall-clock budget for each scan phase (passive recon, asset discovery, crawling, vuln scan, ...). When a phase runs out, remaining tools in it are skipped and the next phase starts fresh.",
      requiresHigher: "Requires a higher plan than {plan}",
      availableOn: "Available on {plan}",
      deepValidation: "⚔ Deep Validation (active testing)",
      dvEnabled: "Runs SQLMap against discovered endpoints with query strings and forms. Active injection testing — may modify or expose data on the target. Only enable with explicit written authorization from the asset owner.",
      dvDisabled: "Disabled: this engagement is not pre-authorized for deep validation at the engagement level. Contact your lead pentester or admin to enable engagement.deep_validation_authorized first.",
      execute: "Execute full pentest",
      dvArmed: "⚠ Deep validation ARMED",
      queued: "⚡ Scan Queued",
      queuedMsg: "Scan #{n} enqueued with {priority} priority",
      queuedHint: "The worker will pick it up shortly. Progress will appear above.",
      error: "Error:",
      portModes: {
        common: "Common (28 ports)",
        extended: "Extended (1000+ ports)",
        full: "Full (65535 ports)",
      },
      timeouts: {
        "300": "5 min (fast scopes)",
        "600": "10 min (balanced)",
        "1800": "30 min (thorough)",
        "3600": "1 hour (large scopes)",
        "7200": "2 hours (enterprise)",
      },
    },
    phases: {
      passive_recon: "🔍 Passive Reconnaissance",
      asset_discovery: "🎯 Asset Discovery",
      fingerprinting: "🔬 Fingerprinting",
      crawling: "🕷 Crawling",
      vuln_scanning: "⚡ Vulnerability Scanning",
      deep_validation: "⚔ Deep Validation",
    },
    liveProgress: {
      statusError: "Status error:",
      unknownError: "Unknown error",
      connecting: "Connecting to scan...",
      cancelTitle: "Cancel this scan?",
      cancelMsg: "Progress collected so far will be preserved. The scan will halt as soon as the current tool exits.",
      cancelConfirm: "Cancel scan",
      cancelKeep: "Keep running",
      noActiveTask: "No active task found",
      cancelled: "Scan cancelled",
      cancelFailed: "Cancel failed:",
      inProgress: "Scan in progress",
      elapsed: "Elapsed: {m}m {s}s",
      evidenceCollected: "Evidence collected",
      stepsCompleted: "Steps completed",
      statusLabel: "Status",
      nowRunning: "NOW RUNNING",
      phaseProgress: "Phase Progress",
      stepsEvidence: "{completed}/{total} steps · {evidence} evidence",
      polling: "Polling every 3 seconds · Status preserved on disk",
      stopScan: "⏹ Stop Scan",
    },
    evidence: {
      loading: "Loading evidence...",
      searchPlaceholder: "Search evidence (title or description)…",
      sortNewest: "Sort: Newest first",
      sortOldest: "Sort: Oldest first",
      sortType: "Sort: Type",
      sortTitle: "Sort: Title",
      sortSeverity: "Sort: Severity",
      all: "All ({n})",
      noMatch: "No evidence matches the current filters.",
    },
    retest: {
      loading: "Loading findings...",
      summary: "Retest Summary",
      toRetest: "To Retest",
      fixedPass: "Fixed (Pass)",
      stillVuln: "Still Vulnerable",
      noConfirmed: "No confirmed findings to retest. Review and confirm findings first.",
      retested: "retested",
      notesLabel: "Retest notes",
      notesPlaceholder: "Tested again on...",
      markFixed: "✔ Fixed (Pass)",
      markVuln: "✘ Still Vulnerable",
      busy: "...",
      markedFixed: "Marked as fixed",
      markedVuln: "Marked as still vulnerable",
      fixedCount: "Fixed ({n})",
      fixedBadge: "FIXED",
    },
    taskHistory: {
      error: "Error:",
      loading: "Loading task history...",
      none: "No background tasks for this engagement yet. Run a scan to create one.",
      cancelTitle: "Cancel this task?",
      cancelMsg: "Progress collected so far is preserved. The scan halts as soon as the current tool exits.",
      cancelConfirm: "Cancel task",
      cancelKeep: "Keep running",
      cancelled: "Task cancelled",
      count: "{n} task(s) · auto-refresh every 5s",
      taskLabel: "Task",
      prioritySuffix: "priority",
      retry: "retry {n}",
      cancelBtn: "⏹ Cancel",
      running: "running...",
      progress: "PROGRESS",
      phase: "PHASE",
      evidence: "EVIDENCE",
      duration: "DURATION",
      created: "Created:",
      done: "Done:",
      statuses: {
        queued: "queued",
        running: "running",
        completed: "completed",
        failed: "failed",
        cancelled: "cancelled",
        retrying: "retrying",
        timeout: "timeout",
      },
      priorities: {
        high: "high",
        normal: "normal",
        low: "low",
      },
    },
    findings: {
      loading: "Loading findings...",
      all: "All ({n})",
      noMatch: "No findings match this filter.",
      statuses: {
        needs_review: "Needs review",
        confirmed: "Confirmed",
        false_positive: "False positive",
        needs_retest: "Needs retest",
        retest_pass: "Retest pass",
        retest_fail: "Retest fail",
        informational: "Informational",
      },
    },
    findingDetail: {
      back: "← Back to findings",
      aiEnriched: "AI enriched",
      descr: "Description",
      impact: "Impact",
      remediation: "Remediation",
      riskAnalysis: "Risk Analysis",
      level: "Level:",
      priority: "Priority:",
      factors: "FACTORS:",
      attackPlaybook: "⚔ Attack Playbook",
      notApplicable: "Not applicable for this finding",
      preconditions: "PRECONDITIONS:",
      attackChain: "ATTACK CHAIN:",
      attackerGains: "Attacker gains: ",
      detectionSignals: "DETECTION SIGNALS:",
      objective: "Objective: ",
      attackPath: "ATTACK PATH:",
      poc: "🔬 Proof of Concept",
      review: "Review",
      status: "STATUS",
      severityOverride: "SEVERITY OVERRIDE",
      reviewerNotes: "Reviewer notes",
      reviewerNotesPh: "Confirmed via manual testing...",
      pocLabel: "Proof of concept",
      pocPh: "curl -v http://target/...",
      saving: "Saving...",
      saveReview: "Save review",
      updated: "Finding updated",
      saveFailed: "Save failed:",
      ownershipToast: "Open the Domains tab on this engagement to verify ownership of the domain.",
    },
    ownership: {
      loadingAssets: "Loading assets…",
      domainOwnership: "Domain ownership",
      domainOwnershipDesc: "AI risk analysis and attack playbooks run only on engagements whose domain ownership has been confirmed. Verify each domain by adding a DNS TXT record OR a small file on its webroot — either passes.",
      addDomain: "Add domain",
      addPlaceholder: "example.com",
      adding: "Adding…",
      add: "Add",
      willScan: "Will scan:",
      unknownError: "Unknown error",
      verified: "✓ verified",
      unverified: "unverified",
      save: "Save",
      cancel: "Cancel",
      destructiveWarn: "⚠ Destructive — this will wipe findings, evidence, and scan history for this engagement, and use up your one-shot domain change. You'll see a confirmation before it's applied.",
      changing: "Changing…",
      applyChange: "Apply change",
      checking: "Checking…",
      verifyOwnership: "Verify ownership",
      readyToScan: "Ready to scan.",
      assetActions: "Asset actions",
      hideInstructions: "Hide verification instructions",
      showInstructions: "Show verification instructions",
      editAsset: "Edit asset value",
      changeDomainMenu: "Change domain (one-time)",
      domainChangeUsed: "Domain change already used for this engagement.",
      newDomainPh: "new-domain.com",
      changeTitle: "Change domain?",
      changeMsg: "Replace \"{old}\" with \"{new}\".\n\nThis will:\n  • Delete all findings, evidence, and scan history for this engagement\n  • Reset ownership verification (you'll need to re-verify the new domain)\n  • Use up your one allowed domain change for this engagement\n\nThis cannot be undone.",
      changeConfirm: "Change domain",
      changeKeep: "Keep current",
      method1: "Method 1 — DNS TXT record",
      recordName: "Record name",
      recordType: "Record type",
      recordValue: "Record value",
      method2: "Method 2 — HTTP file",
      url: "URL",
      fileContents: "File contents",
      instructionsHint: "Add either one and click \"Verify ownership\". Server checks both; whichever passes flips this asset to verified.",
    },
    report: {
      generating: "Generating report...",
      client: "Client",
      generated: "Generated",
      executiveSummary: "Executive summary",
      riskRating: "Risk rating",
      totalFindings: "Total findings",
      evidenceCollected: "Evidence collected",
      assetsTested: "Assets tested",
      severityBreakdown: "Severity breakdown",
      findingsDetail: "Findings detail",
    },
    profile: {
      page: {
        title: "Profile & Settings",
        subtitle: "Manage your account and team",
        sectionAria: "Profile section",
        tabs: {
          profile: "Profile",
          security: "Password & Sessions",
          twofa: "Two-Factor Auth",
          members: "Members",
          billing: "Billing",
        },
      },
      info: {
        anonymous: "Anonymous",
        auth: "Auth:",
        emailVerified: "● Email verified",
        emailNotVerified: "● Email not verified",
        currentOrg: "Current org",
        totalScans: "Total Scans",
        findings: "Findings",
        toolsExecuted: "Tools Executed",
        evidence: "Evidence",
        allOrgs: "All Organizations",
      },
      security: {
        changePassword: "Change Password",
        currentPassword: "Current password",
        newPassword: "New password",
        confirmNewPassword: "Confirm new password",
        noMatch: "New passwords don't match",
        tooShort: "Password must be at least 8 characters",
        changed: "Password changed. Other sessions were revoked.",
        busy: "...",
        changePasswordBtn: "Change Password",
        revokeSessionTitle: "Revoke this session?",
        revokeSessionMsg: "The signed-in browser/device for this session will be logged out the next time it tries to use its current token.",
        revoke: "Revoke",
        sessionRevoked: "Session revoked",
        failed: "Failed:",
        revokeAllTitle: "Revoke all sessions?",
        revokeAllMsg: "Every signed-in browser and device will be logged out — including this one. You'll need to sign in again here right after.",
        revokeAll: "Revoke all",
        activeSessions: "Active Sessions ({n})",
        revokeAllBtn: "Revoke All",
        loading: "Loading...",
        noSessions: "No active sessions",
        unknown: "Unknown",
        createdLabel: "created",
        lastUsedLabel: "last used",
      },
      deleteAccount: {
        soleAdmin: "You're the sole admin of: {names}. Transfer admin rights or delete those orgs before closing your account.",
        dangerZone: "⚠ Danger Zone",
        deleteTitle: "Delete your account",
        deleteDesc: "Permanently closes your account. Your personal data (sessions, 2FA settings, memberships) will be removed. Engagements, findings, and reports remain with your organization but lose the attribution to you. This cannot be undone.",
        deleteBtn: "Delete account",
        confirmTitle: "⚠ Confirm account deletion",
        confirmPre: "This action is permanent. Type ",
        confirmMid: "to confirm, then ",
        with2fa: "verify with your 2FA code",
        withPwd: "enter your password",
        typeToConfirm: "Type \"DELETE\" to confirm",
        deletePlaceholder: "DELETE",
        typeDeleteErr: "Please type DELETE to confirm.",
        stepUpReason: "Enter your 2FA code to permanently delete your account",
        passwordRequired: "Password is required.",
        currentPassword: "Current password",
        deleting: "Deleting...",
        permanentlyDelete: "Permanently delete my account",
        cancel: "Cancel",
      },
    },
    twoFactor: {
      loading: "Loading 2FA status...",
      backupTitle: "✓ Two-Factor Authentication Enabled",
      backupDesc: "Save these backup codes now. They will NOT be shown again. Each code can be used once if you lose access to your authenticator app.",
      copyClipboard: "📋 Copy to Clipboard",
      savedThem: "I've saved them",
      backupCopied: "Backup codes copied to clipboard",
      backupFileHeader: "TrueHacking Two-Factor Backup Codes",
      backupFileFooter: "Keep these somewhere safe. Each code can be used only once.",
      scanQr: "Scan this QR code with your authenticator app",
      qrAlt: "2FA QR Code",
      enterManually: "Or enter this key manually:",
      recommendedApps: "Recommended apps: Google Authenticator, Authy, 1Password, Bitwarden",
      step2: "Step 2 — Enter the 6-digit code from your app",
      verificationCode: "Verification code",
      codePlaceholder: "000000",
      busy: "...",
      activate: "Activate 2FA",
      cancel: "Cancel",
      disableTitle: "⚠ Disable Two-Factor Authentication",
      disableDesc: "This will remove 2FA from your account. You will no longer need a code to sign in. We recommend keeping 2FA enabled — especially for admin accounts.",
      currentPassword: "Current password",
      currentCode: "Current 2FA code (or backup code)",
      disableCodePlaceholder: "000000 or XXXX-XXXX",
      disableBtn: "Disable 2FA",
      requiredLabel: "Required:",
      requiredText: " Superuser accounts must enable two-factor authentication before accessing the platform. Set up 2FA below to continue.",
      twoFactorAuth: "Two-Factor Authentication",
      enabled: "Enabled",
      disabled: "Disabled",
      activatedOn: "Activated",
      backupRemaining: "{n} backup codes remaining",
      considerRegen: " — consider regenerating",
      disable2faBtn: "Disable 2FA",
      enable2faBtn: "Enable 2FA",
      recommendation: "⚠ Recommendation",
      recommendationText: "Enable 2FA to protect your TrueHacking account. Admin operations in the platform panel will require 2FA verification. Scan a QR code with Google Authenticator, Authy, 1Password, or any other TOTP app.",
    },
    billing: {
      loading: "Loading billing…",
      unavailable: "Billing information is unavailable right now.",
      portalError: "Could not open the billing portal.",
      currentPlan: "Current plan",
      billingLabel: "Billing:",
      accessUntil: "Access until {date}",
      renews: "Renews {date}",
      freeTier: "You're on the free tier.",
      changePlan: "Change plan",
      upgrade: "Upgrade",
      opening: "Opening…",
      manageBilling: "Manage billing",
      portalNote: "Manage billing opens your secure Stripe portal — change or cancel your plan, update your card, and download invoices. Payments are processed by Stripe; we never see your card details.",
      statuses: {
        active: "Active",
        trialing: "Trial",
        past_due: "Past due",
        canceled: "Canceled",
        unpaid: "Unpaid",
        incomplete: "Incomplete",
      },
      cycles: {
        yearly: "Annual",
        monthly: "Monthly",
      },
    },
    members: {
      noOrg: "No organization selected",
      roleUpdated: "Role updated",
      failed: "Failed:",
      removeTitle: "Remove member?",
      removeMsg: "{email} will lose access to this organization immediately. Their personal account stays — only the membership is revoked.",
      remove: "Remove",
      memberRemoved: "Member removed",
      membersSuffix: "Members",
      membersCount: "{n} members",
      cancel: "Cancel",
      inviteMember: "+ Invite Member",
      inviteNew: "Invite New Member",
      email: "Email",
      emailPlaceholder: "user@company.com",
      fullNameOptional: "Full name (optional)",
      role: "Role",
      tempPassword: "Temporary password",
      tempPasswordPlaceholder: "Min 8 chars",
      busy: "...",
      createMember: "Create Member",
      loading: "Loading members...",
      active: "● Active",
      disabled: "● Disabled",
      lastLogin: "Last login:",
      never: "Never",
      colUser: "User",
      colStatus: "Status",
      colRole: "Role",
      colLastLogin: "Last login",
      roles: {
        admin: "Admin",
        pentester: "Pentester",
        viewer: "Viewer",
        client: "Client",
      },
    },
    upgrade: {
      title: "Plans & Billing",
      subtitle: "You're on the {plan} plan.",
      checkoutSuccess: "Subscription updated — your plan will reflect shortly.",
      checkoutCancelled: "Checkout cancelled — no charge was made.",
      checkoutError: "Could not start checkout. Try again or contact support.",
      portalError: "Could not open the billing portal.",
      billing: "Billing",
      monthly: "Monthly",
      annual: "Annual",
      currency: "Currency",
      opening: "Opening…",
      manageBilling: "Manage billing",
      loadingPlans: "Loading plans…",
      current: "Current",
      free: "Free",
      custom: "Custom",
      perYr: " / yr",
      perMo: " / mo",
      twoMonthsFree: "2 months free",
      unlimited: "Unlimited",
      scansLabel: "scans / month",
      engagementsLabel: "engagements",
      membersLabel: "team members",
      yourPlan: "Your plan",
      contactSales: "Contact sales",
      choose: "Choose {name}",
      starting: "Starting…",
      footnote: "Annual billing and USD totals (including any discount) are shown securely on the next step. Payments are processed by Stripe — we never see your card details.",
      features: {
        ai_enrichment: "AI risk analysis & attack playbooks",
        deep_validation: "Deep validation (active exploitation)",
        ai_senior_pentester: "Senior-pentester AI tier",
        sso: "Single sign-on (SSO)",
      },
    },
    scanHistory: {
      title: "Scan History",
      subtitle: "Background task history across all engagements",
      errorPrefix: "Error:",
      unknownError: "Unknown error",
      loading: "Loading task history...",
      totalTasks: "Total Tasks",
      activeNow: "Active Now",
      activeSub: "queued + running",
      idleSub: "idle",
      completed: "Completed",
      failed: "Failed",
      noTasks: "No tasks found",
      colNum: "#",
      colEngagement: "Engagement",
      colStatus: "Status",
      colPriority: "Priority",
      colPhase: "Phase / Progress",
      colResults: "Results",
      colDuration: "Duration",
      colWhen: "When",
      retry: "retry {n}",
      evShort: "ev",
      findShort: "find",
      filters: {
        all: "All",
        running: "Running",
        queued: "Queued",
        completed: "Completed",
        failed: "Failed",
        cancelled: "Cancelled",
      },
      statuses: {
        queued: "queued",
        running: "running",
        completed: "completed",
        failed: "failed",
        cancelled: "cancelled",
        retrying: "retrying",
        timeout: "timeout",
      },
      priorities: {
        high: "high",
        normal: "normal",
        low: "low",
      },
      phases: {
        passive_recon: "Passive Recon",
        asset_discovery: "Asset Discovery",
        fingerprinting: "Fingerprinting",
        crawling: "Crawling",
        vuln_scanning: "Vuln Scanning",
        deep_validation: "Deep Validation",
        correlation: "Correlation",
        ai_enrichment: "AI Enrichment",
        completed: "Completed",
        failed: "Failed",
        initializing: "Initializing",
      },
      timeAgo: {
        seconds: "{n}s ago",
        minutes: "{n}m ago",
        hours: "{n}h ago",
      },
    },
    adminPage: {
      platformAdminBadge: "PLATFORM ADMIN",
      stepUpActive: "● Step-up active",
      unresolvedOne: "⚠ {n} unresolved incident",
      unresolvedMany: "⚠ {n} unresolved incidents",
      openHealth: "Open System Health",
      title: "Platform Admin",
      subtitle: "Superuser operations affecting all users and organizations",
      warning: "⚠ Actions in this panel affect ALL users and organizations on the platform. Destructive operations require step-up 2FA verification.",
      sectionAria: "Admin section",
      tabs: {
        dashboard: "Dashboard",
        users: "Users",
        orgs: "Organizations",
        tokens: "Tokens",
        settings: "Settings",
        system: "System",
        health: "Health",
        blog: "Blog",
      },
      gateSubtitle: "Superuser operations",
      gateTitle: "Two-Factor Authentication Required",
      gateBody: "The platform admin panel requires 2FA to be enabled on your account. This is non-negotiable because admin operations can affect every user and organization on the platform. Go to Profile → Two-Factor Auth to set it up.",
    },
    adminStepUp: {
      title: "Step-Up Authentication",
      subtitle: "Re-verify 2FA for admin operations",
      codeLabel: "Current 2FA code (or backup code)",
      codePlaceholder: "000000 or XXXX-XXXX",
      busy: "...",
      verify: "Verify",
      cancel: "Cancel",
      tokenValid: "Token valid for 15 minutes after verification",
    },
    adminDashboard: {
      loading: "Loading dashboard...",
      loadError: "Failed to load dashboard.",
      issuesHealth: "Issues & health",
      unresolvedAlerts: "unresolved security alerts",
      triageHint: "open the Health tab to triage →",
      scansFailed: "scans failed in the last 24h",
      failedLogins: "failed logins in the last 24h",
      bruteForce: "possible brute force",
      jobsBacklogged: "jobs backlogged (scans {scans}, enrich {enrich})",
      diskUsedOn: "disk used on {path}",
      allHealthy: "✓ All systems healthy.",
      queueLabel: "Queue",
      asOf: "as of",
      refresh: "↻ refresh",
      sectionLive: "Live",
      scansToday: "scans today",
      scansInFlight: "scans in flight",
      usersOnline15: "users online (15m)",
      sectionRevenue: "Revenue & growth",
      mrr: "MRR",
      newSubsWk: "new subs (wk)",
      canceledWk: "canceled (wk)",
      trials7d: "trials → 7d",
      planDistribution: "plan distribution",
      newOrgsWk: "new orgs (wk)",
      newOrgsMo: "new orgs (mo)",
      newUsersWk: "new users (wk)",
      freeToPaid: "free → paid",
      sectionUsers: "Users",
      active30d: "active (30d)",
      mfaEnabled: "MFA enabled",
      onlineNow15: "online now (15m)",
      roleDistribution: "role distribution",
      sectionAI: "AI",
      aiTriagedWk: "AI-triaged (wk)",
      aiFpRate: "AI false-positive rate",
      sectionTotals: "Totals",
      orgs: "orgs",
      users: "users",
      engagements: "engagements",
    },
    adminHealth: {
      intro: "Operational incidents the platform recorded automatically. Use the account filter for support (\"customer X reports a problem\"). Resolving an item just acknowledges it — it doesn't fix the underlying cause, but it clears the dash alert.",
      accountSearch: "Account search",
      accountPlaceholder: "email or org name",
      status: "Status",
      statusOpen: "Open",
      statusResolved: "Resolved",
      statusAll: "All",
      statusAria: "Status filter",
      severity: "Severity",
      sevAny: "Any",
      sevCritical: "Critical",
      sevError: "Error",
      sevWarning: "Warning",
      severityAria: "Severity filter",
      kind: "Kind",
      kindAny: "Any",
      kindScanTimeout: "Scan timeout",
      kindEmailFailure: "Email failure",
      kindAiFailure: "AI failure",
      kindAria: "Kind filter",
      loading: "Loading incidents...",
      noIncidents: "No incidents match.",
      nothingOnFire: "Nothing is on fire.",
      rangeOf: "of",
      prev: "Prev",
      next: "Next",
      resolved: "✓ resolved",
      resolve: "Resolve",
      incidentResolved: "Incident resolved",
      failed: "Failed",
      hide: "hide detail",
      detail: "detail",
      whereToLook: "↳ Where to look:",
      kindLabels: {
        scan_timeout: "Scan timeout",
        resend_failure: "Email (Resend) failure",
        ai_enrichment_failure: "AI enrichment failure",
      },
      sevLabels: {
        critical: "Critical",
        error: "Error",
        warning: "Warning",
      },
    },
    adminSystem: {
      refreshed: "Refreshed {n} tools",
      refreshFailed: "Refresh failed:",
      checking: "Checking tool health...",
      overallHealth: "Overall Health",
      toolsInstalled: "{installed}/{total} tools installed",
      installed: "Installed",
      missing: "Missing",
      categories: "Categories",
      checkingBtn: "Checking...",
      refreshBtn: "⟳ Refresh",
      lastCheck: "Last check:",
      cachedFor: "cached for",
      missingBadge: "MISSING",
      colTool: "Tool",
      colVersion: "Version",
      colDescError: "Description / Error",
      colPath: "Path",
      unknown: "unknown",
      aboutTitle: "About this page:",
      aboutPre: "checks are based on ",
      aboutPost: " for presence and a quick subprocess call for version (3-second timeout per tool). Results are cached for 60 seconds. This page only verifies that binaries are installed and responding — it does not test whether they work against real targets. Missing tools are not fatal — the orchestrator logs a warning and skips them, but your coverage suffers silently.",
      categoryLabels: {
        recon: "Passive Recon",
        discovery: "Asset Discovery",
        fingerprinting: "Fingerprinting",
        crawling: "Crawling",
        vuln_scanning: "Vulnerability Scanning",
        deep_validation: "Deep Validation",
      },
    },
    adminSettings: {
      loading: "Loading settings...",
      modified: "● modified",
      noChanges: "No changes",
      settingsSaved: "Settings saved",
      saveFailed: "Save failed:",
      changesReverted: "Changes reverted",
      lastUpdated: "Last updated:",
      never: "never",
      byUser: " by user #{id}",
      revert: "Revert",
      save: "Save ({n} changes)",
      secRegistration: "▸ Registration",
      regEnabled: "Registration enabled",
      regEnabledDesc: "When disabled, new users cannot register via /auth/register. OAuth flows still work. Existing users are unaffected.",
      secOauth: "▸ OAuth Providers",
      googleEnabled: "Google OAuth enabled",
      googleDesc: "Allow users to sign in with Google. Requires credentials in .env (HYDRA_GOOGLE_OAUTH_CLIENT_ID/SECRET).",
      githubEnabled: "GitHub OAuth enabled",
      githubDesc: "Allow users to sign in with GitHub. Requires credentials in .env.",
      secQuotas: "▸ Quotas",
      maxEng: "Max engagements per organization",
      maxEngDesc: "Hard limit on engagements in a single org. Set 0 for unlimited. Enforced on create.",
      unitEng: "eng",
      maxScans: "Max scans per day per user",
      maxScansDesc: "Daily quota (UTC calendar day) on scan executions per user. Set 0 for unlimited. Superusers bypass.",
      unitScans: "scans",
      secAudit: "▸ Audit Log Retention",
      auditRetention: "Audit retention (days)",
      auditRetentionDesc: "Daily cron at 03:00 UTC purges audit entries older than this. Hard floor of 30 days enforced regardless (LGPD).",
      unitDays: "days",
      secAI: "▸ AI Enrichment",
      aiEnabled: "AI enrichment enabled",
      aiDesc: "Use TrueHacking AI to enrich findings with CVSS scoring, attack playbooks, and remediation. Disable if the AI service is unavailable or too slow.",
      noteTitle: "Note:",
      noteBody: "Some settings (JWT secret, CORS origins, HSTS) are intentionally NOT editable here. They live in .env / docker-compose.yml and require a container restart to change. This is a deliberate security design — runtime modification of security boundaries via API is a high-value target for compromised admin accounts.",
    },
    adminTokens: {
      revokeUserTitle: "Revoke all user sessions?",
      revokeUserMsg: "{email} will be logged out from every device on their next request. Their account stays — only the active sessions are killed.",
      revokeSessions: "Revoke sessions",
      revokedFor: "Revoked {n} sessions for {email}",
      revokeFailed: "Revoke failed:",
      mustType: "You must type 'REVOKE ALL' exactly",
      nuclearDone: "☢ REVOKED {n} sessions globally — logging you out",
      nuclearFailed: "Nuclear revoke failed:",
      totalTokens: "Total tokens",
      active: "Active",
      revoked: "Revoked",
      usersWithSessions: "Users w/ sessions",
      filterPlaceholder: "Filter by user_id (number)...",
      includeRevoked: "Include revoked",
      nuclearBtn: "☢ Nuclear: Revoke ALL",
      nuclearTitle: "☢ NUCLEAR OPTION",
      nuclearDesc: "This will revoke EVERY active refresh token on the platform. Every user — including you — will be logged out immediately and must sign in again. Use only in response to a confirmed security incident (suspected JWT secret compromise, credential leak, etc).",
      reasonLabel: "Reason (recorded in audit log)",
      reasonPlaceholder: "e.g. Suspected JWT secret compromise on 2026-04-08",
      typeToConfirm: "Type REVOKE ALL to confirm:",
      execute: "☢ Execute",
      cancel: "Cancel",
      loading: "Loading tokens...",
      noTokens: "No tokens match the current filters",
      statusRevoked: "● Revoked",
      statusActive: "● Active",
      uaPrefix: "UA:",
      ipPrefix: "IP:",
      createdPrefix: "Created:",
      revokeUser: "Revoke User",
      idPrefix: "id",
      colUser: "User",
      colUA: "User Agent",
      colIP: "IP",
      colStatus: "Status",
      colCreated: "Created",
      prev: "← Prev",
      next: "Next →",
      rangeOf: "of",
    },
    adminOrgs: {
      orgCreated: "Organization created",
      createFailed: "Create failed:",
      forceDeleteTitle: "Force-delete organization?",
      deleteTitle: "Delete organization?",
      forceDeleteMsg: "FORCE DELETE {name} including {n} engagements + all findings, evidence, and scan history. Members lose access immediately. This cannot be undone.",
      deleteMsg: "Delete {name}. Fails if the org has active engagements — use Force Delete in that case.",
      forceDelete: "Force delete",
      delete: "Delete",
      orgDeleted: "Organization deleted",
      deleteFailed: "Delete failed:",
      searchPlaceholder: "Search name or slug...",
      orgsCount: "{n} orgs",
      cancel: "Cancel",
      newOrg: "+ New Org",
      createTitle: "Create New Organization",
      nameLabel: "Name",
      namePlaceholder: "Acme Corp",
      descLabel: "Description (optional)",
      createBtn: "Create Organization",
      loading: "Loading orgs...",
      noOrgs: "No organizations found",
      membersLabel: "members",
      engagementsLabel: "engagements",
      colName: "Name",
      colUUID: "UUID",
      colPlan: "Plan",
      colMembers: "Members",
      colEngagements: "Engagements",
      detail: "Detail →",
      prev: "← Prev",
      next: "Next →",
      rangeOf: "of",
      membersStat: "Members",
      engagementsStat: "Engagements",
      byStatus: "By Status",
      membersCount: "Members ({n})",
      actionsStepUp: "Actions (step-up required)",
      deleteSafe: "Delete (safe)",
      forceDeleteCascade: "🗑 Force Delete (cascade)",
    },
    adminUsers: {
      disableTitle: "Disable user?",
      enableTitle: "Enable user?",
      disableMsg: "{email} loses platform access immediately. Their sessions stay valid until their next request, then bounce to login.",
      enableMsg: "{email} regains platform access immediately.",
      disable: "Disable",
      enable: "Enable",
      userDisabled: "User disabled",
      userEnabled: "User enabled",
      failed: "Failed:",
      resetTitle: "Reset password?",
      resetMsg: "{email} gets a freshly-generated password and ALL their sessions are revoked. They'll need to sign in again with the new password — which will be shown to you once and copied to your clipboard.",
      resetConfirm: "Reset password",
      newPwdTitle: "New password generated",
      newPwdMsg: "Copied to your clipboard:\n\n{pwd}\n\nThis is the only time it will be shown. {n} sessions revoked.",
      savedIt: "I saved it",
      pwdReset: "Password reset — {n} sessions revoked",
      resetFailed: "Reset failed:",
      grantTitle: "Grant superuser?",
      revokeTitle: "Revoke superuser?",
      grantMsg: "{email} will GAIN platform-admin access — they'll be able to perform destructive operations on every user and organization.",
      revokeMsg: "{email} will lose platform-admin access — they will no longer reach the admin panel.",
      grantConfirm: "Grant superuser",
      revokeConfirm: "Revoke superuser",
      superUpdated: "Superuser flag updated",
      updateFailed: "Update failed:",
      markVerifiedTitle: "Mark email verified?",
      markVerifiedMsg: "Manually flips email_verified=true for {email}. Use only for support cases (typo at signup, lost inbox access, pre-gate account). The action is recorded in the audit log.",
      markVerifiedConfirm: "Mark verified",
      emailMarkedVerified: "Email marked verified",
      markVerifiedFailed: "Mark verified failed:",
      exported: "Exported user data (LGPD)",
      exportFailed: "Export failed:",
      anonTitle: "Anonymize user (LGPD erasure)?",
      anonMsg: "Right-to-erasure for {email}. This will:\n\n• Replace email with deleted-user-{id}@anonymized.local\n• Wipe name, avatar, credentials\n• Revoke all sessions\n• Remove from all organizations\n\nThis is irreversible. The audit log will record who performed it and when.",
      anonConfirm: "Anonymize",
      userAnonymized: "User anonymized",
      anonFailed: "Anonymize failed:",
      searchPlaceholder: "Search email or name...",
      allStatus: "All status",
      active: "Active",
      disabled: "Disabled",
      allRoles: "All roles",
      superusers: "Superusers",
      regular: "Regular",
      all2fa: "All 2FA",
      twofaEnabledOpt: "2FA enabled",
      twofaDisabledOpt: "2FA disabled",
      usersCount: "{n} users",
      exportCsv: "Export CSV",
      exportOptions: "Export options",
      applyScreenFilters: "Apply on-screen filters",
      includeDisabled: "Include disabled",
      includeUnverified: "Include unverified email",
      exportAlwaysExcludes: "Superusers and anonymized accounts are never exported.",
      downloadCsv: "Download CSV",
      exportingCsv: "Exporting...",
      csvExported: "Users CSV downloaded",
      csvExportFailed: "CSV export failed:",
      loading: "Loading users...",
      noUsers: "No users match the current filters",
      super: "SUPER",
      statusAnonymized: "● Anonymized",
      statusActive: "● Active",
      statusDisabled: "● Disabled",
      emailConfirmed: "Email confirmed",
      emailNotVerified: "Email not yet verified",
      verified: "verified",
      unverified: "unverified",
      twofaOn: "on",
      twofaOff: "off",
      lastLogin: "Last login:",
      never: "Never",
      colUser: "User",
      colStatus: "Status",
      colRole: "Role",
      colVerified: "Verified",
      col2fa: "2FA",
      colLastLogin: "Last Login",
      anon: "● Anon",
      emailConfirmedDesk: "Email confirmed by the user",
      emailNotVerifiedDesk: "User has not clicked the verification link yet",
      yes: "✓ Yes",
      no: "✗ No",
      onLabel: "✓ On",
      offLabel: "Off",
      detail: "Detail →",
      prev: "← Prev",
      next: "Next →",
      rangeOf: "of",
      userNum: "User #{id}",
      statusLabel: "Status",
      superuser: "Superuser",
      yesShort: "Yes",
      noShort: "No",
      twofa: "2FA",
      enabledShort: "Enabled",
      disabledShort: "Disabled",
      authProvider: "Auth Provider",
      activeSessions: "Active Sessions",
      engagementsCreated: "Engagements Created",
      lgpdData: "LGPD Data",
      consent: "Consent:",
      consentVer: "(v{v})",
      preLgpd: "Pre-LGPD account",
      anonymizedAt: "Anonymized:",
      organizations: "Organizations",
      anonNoActions: "This user is anonymized. No further actions available.",
      actionsStepUp: "Actions (step-up required)",
      disableUser: "Disable user",
      enableUser: "Enable user",
      moreActions: "More user actions",
      resetPassword: "Reset password",
      revokeSuperuser: "Revoke superuser",
      grantSuperuser: "Grant superuser",
      markEmailVerified: "Mark email verified",
      exportData: "Export data (LGPD)",
      anonymizeErasure: "Anonymize (LGPD erasure)",
    },
    adminBlog: {
      newPost: "New post",
      editPost: "Edit post",
      noPosts: "No posts yet.",
      loading: "Loading...",
      colTitle: "Title",
      colLocale: "Language",
      colStatus: "Status",
      colUpdated: "Updated",
      draft: "Draft",
      published: "Published",
      edit: "Edit",
      delete: "Delete",
      deleteTitle: "Delete post?",
      deleteMsg: "\"{title}\" will be permanently deleted.",
      deleteConfirm: "Delete",
      deleted: "Post deleted",
      saved: "Post saved",
      saveFailed: "Save failed:",
      fldTitle: "Title",
      fldSlug: "Slug (optional)",
      slugPlaceholder: "auto from title",
      fldLocale: "Language",
      fldExcerpt: "Excerpt (optional)",
      excerptPlaceholder: "Short summary for cards & SEO",
      fldStatus: "Status",
      fldCover: "Cover image",
      uploadCover: "Upload image",
      uploading: "Uploading...",
      removeCover: "Remove",
      uploadFailed: "Upload failed",
      fldBody: "Body",
      cancel: "Cancel",
      save: "Save",
      saving: "Saving...",
      stepUpSave: "Confirm your 2FA to save the post",
      stepUpDelete: "Confirm your 2FA to delete the post",
      stepUpUpload: "Confirm your 2FA to upload the image",
    },
    ui: {
      confirmDelete: "Delete",
      confirm: "Confirm",
      cancel: "Cancel",
      dismiss: "Dismiss",
      teaserPreparing: "AI analysis is being prepared. This usually takes a couple of minutes after a scan completes (or after domain ownership is verified).",
      teaserRefresh: "Refresh",
      verifyOwnership: "Verify domain ownership",
      upgradeUnlock: "Upgrade to unlock",
      preview: "(preview)",
      premiumFeature: "🔒 Premium feature",
      upgradeTitle: "Upgrade your plan",
      upgradeBody: "This feature is available on paid plans. Payments aren't live yet — we're still polishing the billing flow.",
      upgradeContact: "Want early access? Get in touch:",
      gotIt: "Got it",
    },
  },
};

const es: Dictionary = {
  nav: {
    pricing: "Precios",
    platform: "Plataforma",
    contact: "Contacto",
    blog: "Blog",
    login: "Iniciar sesión",
    startFree: "Empezar gratis",
    goToApp: "Ir a la app →",
  },
  blog: {
    meta: {
      title: "Blog — TrueHacking",
      description: "Artículos sobre seguridad ofensiva, pentesting y gestión de superficie de ataque.",
    },
    eyebrow: "BLOG",
    title: "Blog",
    subtitle: "Seguridad ofensiva, pentesting y gestión de superficie de ataque — directo del equipo que construye TrueHacking.",
    readMore: "Leer más →",
    empty: "Aún no hay artículos publicados.",
    loading: "Cargando…",
    backToBlog: "← Volver al blog",
    notFound: "Artículo no encontrado.",
  },
  footer: {
    blurb:
      "Seguridad ofensiva asistida por IA para equipos que entregan rápido y necesitan adelantarse a los atacantes.",
    product: "Producto",
    company: "Empresa",
    pricing: "Precios",
    privacy: "Política de Privacidad",
    terms: "Términos del Servicio",
    rights: "Todos los derechos reservados.",
  },
  landing: {
    meta: {
      title:
        "TrueHacking — Pentesting con IA y Gestión de Superficie de Ataque",
      description:
        "TrueHacking es un copiloto de pentest con IA: reconocimiento continuo, gestión de la superficie de ataque externa, hallazgos con puntuación CVSS y playbooks de ataque realistas — sin esperar semanas por un informe de pentest.",
    },
    hero: {
      eyebrow: "COPILOTO DE PENTEST CON IA",
      tagline:
        "Tu Pentester de IA. Atacando los riesgos de tu SaaS antes que los hackers.",
      subline:
        "Seguridad ofensiva con IA que encuentra lo que los escáneres pasan por alto. Reconocimiento continuo, análisis inteligente de vulnerabilidades, Gestión de Superficie de Ataque Externa y playbooks de ataque para un pentest de verdad — sin esperar semanas por un informe.",
      ctaPrimary: "Empezar gratis",
      ctaPrimaryAuthed: "Abrir panel →",
      seePricing: "Ver precios",
      socialProof:
        "Usado por equipos de seguridad en finanzas, SaaS, pymes y e-commerce.",
    },
    problem: {
      eyebrow: "EL PROBLEMA",
      title: "No puedes defender lo que no puedes ver.",
      points: [
        "No sabes cuántos de tus activos están realmente expuestos a internet.",
        "Los pentests tradicionales son caros, tardan semanas y quedan obsoletos al entregarse.",
        "Tus herramientas te ahogan en hallazgos de baja prioridad sin decirte qué corregir primero.",
        "Credenciales, archivos de configuración y servicios internos se filtran por subdominios olvidados que nunca ves.",
        "Pagas por WAFs, EDRs y SOCs pero aún no puedes responder '¿estamos realmente seguros?'",
      ],
    },
    platform: {
      eyebrow: "LA PLATAFORMA",
      title: "Una herramienta para cubrir toda tu superficie de ataque",
      subtitle:
        "TrueHacking automatiza lo que un pentester sénior haría a mano y suma IA encima para priorizar y explicar cada hallazgo.",
      features: [
        {
          title: "Reconocimiento continuo",
          body: "Mapea tu superficie de ataque externa y monitoréala 24/7. Subdominios, puertos, servicios y activos olvidados — descubiertos y rastreados automáticamente.",
        },
        {
          title: "Análisis con IA",
          body: "Cada hallazgo se enriquece con puntuación CVSS, factores de riesgo contextuales y un playbook de ataque realista. Sin el ruido de falsos positivos.",
        },
        {
          title: "Simulación de cadena de explotación",
          body: "Ve exactamente cómo un atacante encadenaría vulnerabilidades de principio a fin. Prioriza lo que de verdad importa, no solo lo que es grave en el papel.",
        },
        {
          title: "Informes en un clic",
          body: "Resumen ejecutivo, detalle técnico, comandos de prueba de concepto. Listo para auditores, clientes y tu directorio — en una exportación.",
        },
      ],
    },
    how: {
      eyebrow: "CÓMO FUNCIONA",
      title: "Tres pasos de cero a hallazgos accionables",
      steps: [
        {
          title: "Crea un engagement",
          body: "Apunta TrueHacking a un dominio, rango de IP o tenant de nube. Elige un perfil de scan — web, API, red, nube, móvil, IoT o sistemas de IA/ML.",
        },
        {
          title: "Ejecuta el scan",
          body: "Siete fases automatizadas: recon pasivo, descubrimiento de activos, fingerprinting, crawling, escaneo de vulnerabilidades, validación profunda. Progreso en vivo mientras corre.",
        },
        {
          title: "Revisa y actúa",
          body: "Los hallazgos se correlacionan, puntúan y priorizan. La revisión humana confirma los críticos. Exporta un informe cuando termines.",
        },
      ],
    },
    ai: {
      eyebrow: "TRUEHACKING AI",
      title: "El cerebro de un pentester sénior en cada hallazgo",
      body: "Cada vulnerabilidad se analiza en contexto: puntuación CVSS v3.1 con justificación, un playbook de ataque con pasos de explotación realistas, comandos de reproducción y remediación adaptada a tu stack. Sin copiar y pegar genérico. Sin spam de falsos positivos. Solo las tres cosas que un pentester te diría si tuviera todo el día.",
    },
    pricing: {
      eyebrow: "PRECIOS",
      title: "Empieza gratis. Mejora cuando lo necesites.",
      subtitle:
        "El plan gratis te pone en marcha sin tarjeta de crédito. Sin sorpresas por usuario — el precio escala con scans y funciones.",
      loading: "Cargando planes…",
      perMonth: "por mes",
      custom: "A medida",
      mostPopular: "Más popular",
      engagements: "{n} proyectos",
      scans: "{n} scans / mes",
      users: "{n} usuarios",
      unlimited: "Ilimitado",
      ctaFree: "Empezar gratis",
      ctaEnterprise: "Hablar con ventas",
      ctaGet: "Obtener {name}",
      monthly: "Mensual",
      annual: "Anual",
      save: "2 meses gratis",
      perYear: "por año",
    },
    featureLabels: {
      ai_enrichment: "Análisis de riesgo con IA",
      ai_senior_pentester: "Pentester Sénior de IA",
      deep_validation: "Validación profunda (SQLMap, pruebas activas)",
      support_low: "Soporte de baja prioridad",
      support_5x8: "Soporte 5x8",
      support_high: "Soporte de alta prioridad",
      sso: "Inicio de sesión único (SSO)",
    },
    finalCta: {
      title: "¿Listo para ver tu superficie de ataque real?",
      body: "Crea una cuenta en treinta segundos. Ejecuta tu primer scan en cinco minutos. Empieza gratis — sin tarjeta de crédito, sin llamada con un vendedor.",
      button: "Empezar gratis →",
      buttonAuthed: "Abrir panel →",
    },
  },

  platform: {
    meta: {
      title: "Plataforma — TrueHacking",
      description: "Del recon al informe: descubrimiento de superficie, exposiciones filtradas, validación activa autorizada, correlación, hallazgos explicados por IA e informes listos para el cliente.",
    },
    hero: {
      eyebrow: "LA PLATAFORMA",
      title: "Una plataforma de seguridad ofensiva, del recon al informe.",
      subline: "Reunimos el mejor arsenal ofensivo y le sumamos IA encima — para descubrir, validar y explicar riesgos reales, contigo al control de cada prueba activa.",
    },
    capabilities: {
      eyebrow: "CAPACIDADES",
      title: "Todo lo que necesitas para un engagement completo.",
      items: [
        { title: "Mapea toda la superficie de ataque", body: "Antes de tocar nada, la plataforma descubre lo que está expuesto: subdominios, DNS, puertos abiertos, tecnologías en uso y presencia de WAF." },
        { title: "Encuentra lo que ya se filtró", body: "Secretos olvidados en repositorios, correos en filtraciones conocidas, buckets abiertos y typosquatting — todo lo que un atacante encontraría primero." },
        { title: "Valida de verdad — solo cuando lo autorizas", body: "Las pruebas activas (como SQLMap) corren solo dentro del alcance del engagement y solo tras tu autorización explícita." },
        { title: "Muchas herramientas, una fuente de verdad", body: "Todo el arsenal alimenta un correlation engine que deduplica y conecta los resultados en una vista única, sin ruido." },
        { title: "Hallazgos que se explican solos", body: "La IA reescribe cada hallazgo con descripción, impacto y remediación. El AI Senior Pentester valida y guía la confirmación, al nivel de un consultor sénior." },
        { title: "Informe ejecutivo, listo para entregar", body: "Risk rating, desglose por severidad, evidencias y apéndice técnico — formateado y exportable. Del hallazgo al PDF del cliente sin retrabajo." },
        { title: "Varios clientes, cuentas aisladas", body: "Orgs y workspaces separados, roles por usuario y panel admin. SSO en Enterprise. Cada cliente en su caja, sin fugas entre cuentas." },
      ],
    },
    workflow: {
      eyebrow: "EL FLUJO",
      title: "Cómo ocurre un engagement",
      steps: [
        "Define el alcance y los objetivos autorizados.",
        "Se mapea la superficie y se levantan las exposiciones.",
        "Pruebas activas solo tras tu autorización.",
        "Cada resultado se vuelve un finding con impacto y remediación.",
        "Tú confirmas qué entra en el informe.",
        "Exporta en PDF, listo para el cliente.",
      ],
    },
    control: {
      eyebrow: "CONTROL",
      title: "Poder ofensivo, siempre bajo tu mando.",
      items: [
        { title: "Autorización explícita", body: "Las pruebas activas solo corren cuando las autorizas, por engagement." },
        { title: "Human-in-the-loop", body: "Nada entra en el informe sin tu revisión y aprobación." },
        { title: "Aislamiento por cuenta", body: "Orgs y workspaces aislados, con roles y panel admin." },
      ],
    },
    cta: {
      eyebrow: "EMPIEZA AHORA",
      title: "Ve la plataforma en acción.",
      body: "Empieza en el plan gratis o agenda una demostración.",
      button: "Empezar gratis →",
    },
  },

  pricing: {
    meta: { title: "Precios — TrueHacking", description: "Precio que escala contigo, no con tu equipo. Empieza gratis, sin tarjeta. Pagas por scans y funciones, no por asiento." },
    hero: { eyebrow: "PRECIOS", title: "Precio que escala contigo, no con tu equipo.", lead: "Empieza gratis, sin tarjeta. Sin sorpresas por asiento — pagas por scans y funciones, no por persona." },
    comparison: {
      eyebrow: "COMPARACIÓN",
      title: "Compara los planes en detalle",
      groupLimits: "Límites",
      groupFeatures: "Funciones",
      engagements: "Proyectos",
      scans: "Scans por mes",
      users: "Usuarios",
    },
    cta: { eyebrow: "EMPIEZA AHORA", title: "Empieza gratis hoy. Mejora cuando lo necesites.", body: "Sin tarjeta de crédito. Sin sorpresas por asiento.", buttonFree: "Empezar gratis", buttonSales: "Hablar con ventas" },
  },

  app: {
    nav: {
      dashboard: "Panel",
      engagements: "Proyectos",
      history: "Historial de scans",
      profile: "Perfil y uso",
      upgrade: "Mejorar plan",
      admin: "Admin de la plataforma",
    },
    adminBadge: "ADMIN",
    logout: "Salir",
    closeMenu: "Cerrar menú",
    tagline: "Piensa como muchos.\nAtaca como uno.",
    aiStatus: "TrueHacking AI",
    services: "Servicios",
    auth: {
      copilot: "Copiloto de Pentest con IA",
      tagline: "Piensa como muchos. Ataca como uno.",
      signIn: "Iniciar sesión",
      createAccount: "Crear cuenta",
      signInSub: "Accede a tu workspace de pentest",
      registerSub: "Empieza tu primer workspace",
      twofaTitle: "Verificación en dos pasos",
      twofaHeading: "Ingresa el código de 6 dígitos",
      twofaHint: "Abre tu app de autenticación e ingresa el código. También puedes usar un código de respaldo.",
      codeLabel: "Código",
      codePlaceholder: "000000 o XXXX-XXXX",
      verify: "Verificar",
      busy: "...",
      backToSignin: "Volver al inicio de sesión",
      google: "Continuar con Google",
      github: "Continuar con GitHub",
      or: "o",
      email: "Correo",
      emailPlaceholder: "tu@empresa.com",
      password: "Contraseña",
      passwordPlaceholder: "••••••••",
      fullName: "Nombre completo",
      fullNamePlaceholder: "Juan Pérez",
      org: "Nombre de la organización",
      orgPlaceholder: "Acme Security",
      consentPre: "Acepto los",
      terms: "Términos del Servicio",
      consentAnd: "y la",
      privacy: "Política de Privacidad",
      consentPost: ", y consiento el tratamiento de mis datos personales como se describe. (LGPD Art. 7º, I)",
      needAccount: "¿No tienes cuenta? Regístrate",
      haveAccount: "¿Ya tienes cuenta? Inicia sesión",
      forgot: "¿Olvidaste la contraseña?",
      noOauth: "Proveedores OAuth no configurados",
      recoverySubtitle: "Recuperación de contraseña",
      checkEmail: "Revisa tu correo",
      forgotSentPre: "Si existe una cuenta con",
      forgotSentPost: ", te enviamos un enlace para restablecer la contraseña. El enlace expira en 15 minutos.",
      forgotHeading: "¿Olvidaste tu contraseña?",
      forgotSub: "Ingresa tu correo y te enviaremos un enlace para restablecerla.",
      sendReset: "Enviar enlace de restablecimiento",
      resetSubtitle: "Definir nueva contraseña",
      missingToken: "Falta el token de restablecimiento. Redirigiendo al inicio de sesión...",
      pwdUpdatedTitle: "Contraseña actualizada",
      pwdUpdatedBody: "Tu contraseña se ha cambiado. Todas las sesiones activas se cerraron. Redirigiendo al inicio de sesión...",
      signInNow: "Iniciar sesión ahora →",
      chooseNewPwd: "Elige una nueva contraseña",
      chooseNewPwdSub: "Al menos 8 caracteres. Elige algo memorable.",
      newPassword: "Nueva contraseña",
      confirmPassword: "Confirmar contraseña",
      updatePassword: "Actualizar contraseña",
      errPwdShort: "La contraseña debe tener al menos 8 caracteres.",
      errPwdMismatch: "Las contraseñas no coinciden.",
      verifySubtitle: "Verificación de correo",
      verifying: "Verificando tu correo...",
      verifiedTitle: "✓ Correo verificado",
      verifiedConfirmed: "está confirmado.",
      verifiedSignedIn: " Ya tienes sesión iniciada — continúa a tu panel.",
      verifiedSignIn: " Inicia sesión para continuar.",
      goDashboard: "Ir al panel →",
      continueSignIn: "Continuar al inicio de sesión",
      linkInvalidTitle: "Enlace inválido o expirado",
      noTokenMsg: "No hay token de verificación en el enlace. Abre el enlace de tu correo nuevamente.",
      invalidMsg: "Este enlace de verificación es inválido o ha expirado. Solicita uno nuevo.",
      requestNewLink: "Solicitar un nuevo enlace",
    },
    openMenu: "Abrir menú",
    errors: {
      notFoundCode: "ERROR 404 · NO ENCONTRADO",
      notFoundTitle: "No encontramos esa página.",
      notFoundMsg: "El enlace puede estar roto o la página pudo haberse movido. Revisa la URL o vuelve a un punto de partida conocido.",
      goHome: "Ir al inicio",
      openDashboard: "Abrir panel",
      unavailableCode: "ERROR 503 · SERVICIO NO DISPONIBLE",
      unavailableTitle: "Tenemos problemas para alcanzar nuestros servidores.",
      unavailableMsg: "La plataforma está temporalmente no disponible — puede que estemos desplegando una actualización o recuperándonos de un incidente. Verificamos cada 5 segundos y te llevaremos de vuelta en cuanto esté en línea.",
      tryNow: "Intentar ahora",
      backDashboard: "Volver al panel",
      backHome: "Volver al inicio",
      errorCode: "ERROR 500 · ERROR INESPERADO",
      errorTitle: "Algo salió mal.",
      errorMsg: "Encontramos un problema inesperado al renderizar esta página. Recargar suele solucionarlo; si no, vuelve al inicio y ábrela de nuevo.",
      reload: "Recargar página",
    },
    verifyRequired: {
      vrTitle: "Verifica tu correo",
      oneMoreStep: "Un paso más",
      sentPre: "Enviamos un enlace de verificación a",
      sentPost: ". Haz clic en él para terminar de configurar tu cuenta y vuelve para iniciar sesión. El enlace es válido por 3 días.",
      unsentPre: "Para terminar de configurar tu cuenta, haz clic abajo para enviar un enlace de verificación a",
      unsentPost: ". El enlace será válido por 3 días.",
      sending: "Enviando...",
      resendIn: "Reenviar en {t}",
      resendEmail: "Reenviar correo de verificación",
      sendEmail: "Enviar correo de verificación",
      deliveryFailedPre: "No pudimos entregar el correo ahora. Inténtalo de nuevo en unos minutos — si sigue fallando, contacta",
      deliveryFailedPost: ".",
      coldBody: "No sabemos qué cuenta intentas verificar en este dispositivo. Inicia sesión con las credenciales que registraste — si tu correo aún no está verificado, volverás aquí automáticamente con un botón de Reenviar.",
      goSignIn: "Ir al inicio de sesión",
      signOutOther: "Cerrar sesión y usar otra cuenta",
      backToSignin: "← Volver al inicio de sesión",
      hintPre: "¿No lo recibiste? Revisa tu carpeta de spam. El enlace se envía desde",
      hintMid: ". Si escribiste mal tu correo al registrarte, contacta",
      hintPost: ".",
      tooSoon: "Demasiado pronto — inténtalo de nuevo en {t}.",
      cantSend: "No se pudo enviar ahora. Inténtalo de nuevo en unos minutos — si sigue fallando, contacta a soporte.",
      freshLink: "Un nuevo enlace está en camino.",
      couldNotRequest: "No se pudo solicitar un nuevo enlace.",
    },
    dashboard: {
      loading: "Cargando panel...",
      error: "Error:",
      unknownError: "Error desconocido",
      title: "Panel",
      subtitle: "Vista en tiempo real de tu plataforma TrueHacking",
      plan: "Plan",
      scansThisMonth: "Scans este mes",
      usageEngagements: "Proyectos",
      totalEngagements: "Total de proyectos",
      thisWeek: "+{n} esta semana",
      totalFindings: "Total de hallazgos",
      criticalPending: "{n} críticos pendientes",
      evidenceCollected: "Evidencias recopiladas",
      avgScanDuration: "Duración media del scan",
      successRate: "{n}% de éxito",
      currentlyScanning: "Escaneando ahora ({n})",
      noScans: "Ningún scan en curso",
      started: "iniciado",
      recently: "recientemente",
      live: "EN VIVO",
      serviceHealth: "Salud de servicios",
      findingsBySeverity: "Hallazgos por severidad",
      pentestTypes: "Tipos de pentest",
      noData: "Sin datos aún",
      findingsTrend: "Tendencia de hallazgos (últimos 14 días)",
      criticalFindings: "⚠ Hallazgos críticos (revisión pendiente)",
      noneGreat: "Ninguno — ¡genial!",
      recentEngagements: "Proyectos recientes",
      noEngagements: "Ningún proyecto aún",
      topTools: "Top herramientas (más usadas)",
      runs: "{n} ejecuciones",
      executionStats: "Estadísticas de ejecución",
      totalExecutions: "Total de ejecuciones",
      successful: "Exitosas",
      errorsTimeouts: "Errores / Timeouts",
      successRateLabel: "Tasa de éxito",
      errorRate: "Tasa de error",
    },
    engagements: {
      loading: "Cargando proyectos...",
      error: "Error:",
      unknownError: "Error desconocido",
      title: "Proyectos",
      subtitle: "{total} en total · {running} en ejecución (cargados)",
      planLimit: "Límite del plan alcanzado ({used}/{max}). Mejora o archiva uno.",
      planLimitReached: "🔒 Límite del plan alcanzado",
      newEngagement: "+ Nuevo proyecto",
      searchPlaceholder: "Buscar por nombre, cliente u objetivo…",
      sortNewest: "Ordenar: Más recientes",
      sortOldest: "Ordenar: Más antiguos",
      sortName: "Ordenar: Nombre",
      sortStatus: "Ordenar: Estado",
      statusLabel: "Estado",
      companyAll: "Empresa: Todas",
      noMatch: "Ningún proyecto coincide con los filtros actuales.",
      noClient: "Sin cliente",
      showing: "Mostrando {n} de {total}",
      loadMore: "Cargar más",
      loadingMore: "Cargando…",
      statuses: {
        all: "todos",
        draft: "borrador",
        active: "activo",
        scanning: "escaneando",
        review: "revisión",
        retesting: "reprueba",
        completed: "completado",
        archived: "archivado",
      },
    },
    newEngagement: {
      pickTitle: "¿Qué tipo de pentest?",
      pickSubtitle: "Esto determina qué herramientas y estrategias de scan usará TrueHacking",
      bcEngagements: "Proyectos",
      bcNew: "Nuevo",
      detailsTitle: "Pentest de {label}",
      detailsSubtitle: "Configura los detalles del proyecto y los objetivos",
      changeType: "← Cambiar tipo",
      nameLabel: "Nombre del proyecto",
      namePlaceholder: "Pentest Externo Q2 2026",
      clientLabel: "Nombre del cliente",
      clientPlaceholder: "Acme Corp",
      scopeLabel: "Descripción del alcance",
      scopePlaceholder: "Aplicaciones web y APIs externas",
      targetsLabel: "Objetivos (uno por línea)",
      error: "Error:",
      unknown: "Desconocido",
      creating: "Creando...",
      createContinue: "Crear y continuar",
      types: {
        web_application: { label: "Aplicación Web", description: "Pentest completo de web app — XSS, SQLi, bypass de auth" },
        api: { label: "Seguridad de API", description: "REST/GraphQL — auth, inyección, rate limiting" },
        network: { label: "Red", description: "Escaneo de puertos, enumeración de servicios, CVEs conocidos" },
        cloud: { label: "Nube", description: "Buckets S3, servicios expuestos, configuraciones incorrectas" },
        mobile: { label: "Mobile", description: "Backend de app móvil — endpoints de API, auth" },
        iot: { label: "IoT", description: "Credenciales por defecto, servicios expuestos, firmware" },
        ai: { label: "IA/ML", description: "Endpoints de modelo, exposición de datos, prompt injection" },
      },
    },
    engagementDetail: {
      loading: "Cargando proyecto...",
      noClient: "Sin cliente",
      created: "Creado el",
      bcEngagements: "Proyectos",
      sectionAria: "Sección del proyecto",
      tabs: {
        overview: "Resumen",
        scanning: "⚡ Escaneando...",
        scan: "Scan",
        domains: "Dominios",
        findings: "Hallazgos ({n})",
        evidence: "Evidencias",
        retest: "Reprueba",
        history: "Historial de tareas",
        report: "Informe",
      },
    },
    severities: {
      critical: "Crítico",
      high: "Alto",
      medium: "Medio",
      low: "Bajo",
      informational: "Info",
    },
    overview: {
      loading: "Cargando resumen...",
      assets: "Activos ({n})",
      reviewProgress: "Progreso de revisión",
      needsReview: "Necesita revisión",
      confirmed: "Confirmado",
      falsePositive: "Falso positivo",
      needsRetest: "Necesita reprueba",
      runScan: "Ejecutar scan",
      latestFindings: "Últimos hallazgos",
    },
    scan: {
      config: "Configuración del scan",
      portMode: "MODO DE SCAN DE PUERTOS",
      phaseBudget: "PRESUPUESTO POR FASE",
      phaseBudgetHelp: "Tiempo de reloj para cada fase del scan (recon pasivo, descubrimiento de activos, crawling, escaneo de vulns, ...). Cuando una fase se agota, las herramientas restantes se omiten y la siguiente fase empieza de cero.",
      requiresHigher: "Requiere un plan superior a {plan}",
      availableOn: "Disponible en {plan}",
      deepValidation: "⚔ Validación profunda (prueba activa)",
      dvEnabled: "Ejecuta SQLMap contra endpoints descubiertos con query strings y formularios. Prueba de inyección activa — puede modificar o exponer datos en el objetivo. Actívala solo con autorización explícita por escrito del dueño del activo.",
      dvDisabled: "Deshabilitado: este proyecto no está preautorizado para validación profunda a nivel del engagement. Contacta a tu pentester líder o admin para habilitar engagement.deep_validation_authorized primero.",
      execute: "Ejecutar pentest completo",
      dvArmed: "⚠ Validación profunda ARMADA",
      queued: "⚡ Scan en cola",
      queuedMsg: "Scan #{n} encolado con prioridad {priority}",
      queuedHint: "El worker lo tomará en breve. El progreso aparecerá arriba.",
      error: "Error:",
      portModes: {
        common: "Común (28 puertos)",
        extended: "Extendido (1000+ puertos)",
        full: "Completo (65535 puertos)",
      },
      timeouts: {
        "300": "5 min (alcances rápidos)",
        "600": "10 min (equilibrado)",
        "1800": "30 min (minucioso)",
        "3600": "1 hora (alcances grandes)",
        "7200": "2 horas (enterprise)",
      },
    },
    phases: {
      passive_recon: "🔍 Reconocimiento pasivo",
      asset_discovery: "🎯 Descubrimiento de activos",
      fingerprinting: "🔬 Fingerprinting",
      crawling: "🕷 Crawling",
      vuln_scanning: "⚡ Escaneo de vulnerabilidades",
      deep_validation: "⚔ Validación profunda",
    },
    liveProgress: {
      statusError: "Error de estado:",
      unknownError: "Error desconocido",
      connecting: "Conectando al scan...",
      cancelTitle: "¿Cancelar este scan?",
      cancelMsg: "El progreso recopilado hasta ahora se conservará. El scan se detendrá en cuanto la herramienta actual termine.",
      cancelConfirm: "Cancelar scan",
      cancelKeep: "Mantener en ejecución",
      noActiveTask: "No se encontró tarea activa",
      cancelled: "Scan cancelado",
      cancelFailed: "Error al cancelar:",
      inProgress: "Scan en curso",
      elapsed: "Transcurrido: {m}m {s}s",
      evidenceCollected: "Evidencias recopiladas",
      stepsCompleted: "Pasos completados",
      statusLabel: "Estado",
      nowRunning: "EJECUTANDO AHORA",
      phaseProgress: "Progreso de fases",
      stepsEvidence: "{completed}/{total} pasos · {evidence} evidencias",
      polling: "Actualizando cada 3 segundos · Estado preservado en disco",
      stopScan: "⏹ Detener scan",
    },
    evidence: {
      loading: "Cargando evidencias...",
      searchPlaceholder: "Buscar evidencias (título o descripción)…",
      sortNewest: "Ordenar: Más recientes",
      sortOldest: "Ordenar: Más antiguas",
      sortType: "Ordenar: Tipo",
      sortTitle: "Ordenar: Título",
      sortSeverity: "Ordenar: Severidad",
      all: "Todas ({n})",
      noMatch: "Ninguna evidencia coincide con los filtros actuales.",
    },
    retest: {
      loading: "Cargando hallazgos...",
      summary: "Resumen de reprueba",
      toRetest: "Para reprobar",
      fixedPass: "Corregidos (Pass)",
      stillVuln: "Aún vulnerable",
      noConfirmed: "No hay hallazgos confirmados para reprobar. Revisa y confirma los hallazgos primero.",
      retested: "reprobado",
      notesLabel: "Notas de reprueba",
      notesPlaceholder: "Probado de nuevo el...",
      markFixed: "✔ Corregido (Pass)",
      markVuln: "✘ Aún vulnerable",
      busy: "...",
      markedFixed: "Marcado como corregido",
      markedVuln: "Marcado como aún vulnerable",
      fixedCount: "Corregidos ({n})",
      fixedBadge: "CORREGIDO",
    },
    taskHistory: {
      error: "Error:",
      loading: "Cargando historial de tareas...",
      none: "Aún no hay tareas en segundo plano para este proyecto. Ejecuta un scan para crear una.",
      cancelTitle: "¿Cancelar esta tarea?",
      cancelMsg: "El progreso recopilado hasta ahora se conserva. El scan se detiene en cuanto la herramienta actual termina.",
      cancelConfirm: "Cancelar tarea",
      cancelKeep: "Mantener en ejecución",
      cancelled: "Tarea cancelada",
      count: "{n} tarea(s) · actualización automática cada 5s",
      taskLabel: "Tarea",
      prioritySuffix: "prioridad",
      retry: "reintento {n}",
      cancelBtn: "⏹ Cancelar",
      running: "ejecutando...",
      progress: "PROGRESO",
      phase: "FASE",
      evidence: "EVIDENCIAS",
      duration: "DURACIÓN",
      created: "Creado:",
      done: "Hecho:",
      statuses: {
        queued: "en cola",
        running: "ejecutando",
        completed: "completado",
        failed: "falló",
        cancelled: "cancelado",
        retrying: "reintentando",
        timeout: "timeout",
      },
      priorities: {
        high: "alta",
        normal: "normal",
        low: "baja",
      },
    },
    findings: {
      loading: "Cargando hallazgos...",
      all: "Todos ({n})",
      noMatch: "Ningún hallazgo coincide con este filtro.",
      statuses: {
        needs_review: "Necesita revisión",
        confirmed: "Confirmado",
        false_positive: "Falso positivo",
        needs_retest: "Necesita reprueba",
        retest_pass: "Reprueba OK",
        retest_fail: "Reprueba falló",
        informational: "Informativo",
      },
    },
    findingDetail: {
      back: "← Volver a los hallazgos",
      aiEnriched: "Enriquecido por IA",
      descr: "Descripción",
      impact: "Impacto",
      remediation: "Remediación",
      riskAnalysis: "Análisis de riesgo",
      level: "Nivel:",
      priority: "Prioridad:",
      factors: "FACTORES:",
      attackPlaybook: "⚔ Playbook de ataque",
      notApplicable: "No aplicable para este hallazgo",
      preconditions: "PRECONDICIONES:",
      attackChain: "CADENA DE ATAQUE:",
      attackerGains: "El atacante obtiene: ",
      detectionSignals: "SEÑALES DE DETECCIÓN:",
      objective: "Objetivo: ",
      attackPath: "RUTA DE ATAQUE:",
      poc: "🔬 Prueba de concepto",
      review: "Revisión",
      status: "ESTADO",
      severityOverride: "SOBRESCRIBIR SEVERIDAD",
      reviewerNotes: "Notas del revisor",
      reviewerNotesPh: "Confirmado mediante prueba manual...",
      pocLabel: "Prueba de concepto",
      pocPh: "curl -v http://objetivo/...",
      saving: "Guardando...",
      saveReview: "Guardar revisión",
      updated: "Hallazgo actualizado",
      saveFailed: "Error al guardar:",
      ownershipToast: "Abre la pestaña Dominios en este proyecto para verificar la propiedad del dominio.",
    },
    ownership: {
      loadingAssets: "Cargando activos…",
      domainOwnership: "Propiedad del dominio",
      domainOwnershipDesc: "El análisis de riesgo por IA y los playbooks de ataque se ejecutan solo en proyectos cuya propiedad del dominio ha sido confirmada. Verifica cada dominio agregando un registro DNS TXT O un pequeño archivo en su webroot — cualquiera sirve.",
      addDomain: "Agregar dominio",
      addPlaceholder: "ejemplo.com",
      adding: "Agregando…",
      add: "Agregar",
      willScan: "Escaneará:",
      unknownError: "Error desconocido",
      verified: "✓ verificado",
      unverified: "no verificado",
      save: "Guardar",
      cancel: "Cancelar",
      destructiveWarn: "⚠ Destructivo — esto borrará hallazgos, evidencias e historial de scan de este proyecto, y consumirá tu cambio de dominio único. Verás una confirmación antes de aplicar.",
      changing: "Cambiando…",
      applyChange: "Aplicar cambio",
      checking: "Verificando…",
      verifyOwnership: "Verificar propiedad",
      readyToScan: "Listo para escanear.",
      assetActions: "Acciones del activo",
      hideInstructions: "Ocultar instrucciones de verificación",
      showInstructions: "Mostrar instrucciones de verificación",
      editAsset: "Editar valor del activo",
      changeDomainMenu: "Cambiar dominio (una vez)",
      domainChangeUsed: "Cambio de dominio ya usado para este proyecto.",
      newDomainPh: "nuevo-dominio.com",
      changeTitle: "¿Cambiar dominio?",
      changeMsg: "Reemplazar \"{old}\" por \"{new}\".\n\nEsto hará:\n  • Eliminar todos los hallazgos, evidencias e historial de scan de este proyecto\n  • Restablecer la verificación de propiedad (deberás reverificar el nuevo dominio)\n  • Usar tu único cambio de dominio permitido para este proyecto\n\nEsto no se puede deshacer.",
      changeConfirm: "Cambiar dominio",
      changeKeep: "Mantener actual",
      method1: "Método 1 — registro DNS TXT",
      recordName: "Nombre del registro",
      recordType: "Tipo del registro",
      recordValue: "Valor del registro",
      method2: "Método 2 — archivo HTTP",
      url: "URL",
      fileContents: "Contenido del archivo",
      instructionsHint: "Agrega cualquiera y haz clic en \"Verificar propiedad\". El servidor revisa ambos; el que pase marca este activo como verificado.",
    },
    report: {
      generating: "Generando informe...",
      client: "Cliente",
      generated: "Generado",
      executiveSummary: "Resumen ejecutivo",
      riskRating: "Clasificación de riesgo",
      totalFindings: "Total de hallazgos",
      evidenceCollected: "Evidencias recopiladas",
      assetsTested: "Activos probados",
      severityBreakdown: "Distribución por severidad",
      findingsDetail: "Detalle de hallazgos",
    },
    profile: {
      page: {
        title: "Perfil y configuración",
        subtitle: "Gestiona tu cuenta y equipo",
        sectionAria: "Sección del perfil",
        tabs: {
          profile: "Perfil",
          security: "Contraseña y sesiones",
          twofa: "Autenticación 2FA",
          members: "Miembros",
          billing: "Facturación",
        },
      },
      info: {
        anonymous: "Anónimo",
        auth: "Auth:",
        emailVerified: "● Correo verificado",
        emailNotVerified: "● Correo no verificado",
        currentOrg: "Organización actual",
        totalScans: "Total de scans",
        findings: "Hallazgos",
        toolsExecuted: "Herramientas ejecutadas",
        evidence: "Evidencias",
        allOrgs: "Todas las organizaciones",
      },
      security: {
        changePassword: "Cambiar contraseña",
        currentPassword: "Contraseña actual",
        newPassword: "Nueva contraseña",
        confirmNewPassword: "Confirmar nueva contraseña",
        noMatch: "Las nuevas contraseñas no coinciden",
        tooShort: "La contraseña debe tener al menos 8 caracteres",
        changed: "Contraseña cambiada. Las demás sesiones fueron revocadas.",
        busy: "...",
        changePasswordBtn: "Cambiar contraseña",
        revokeSessionTitle: "¿Revocar esta sesión?",
        revokeSessionMsg: "El navegador/dispositivo conectado en esta sesión se cerrará la próxima vez que intente usar su token actual.",
        revoke: "Revocar",
        sessionRevoked: "Sesión revocada",
        failed: "Falló:",
        revokeAllTitle: "¿Revocar todas las sesiones?",
        revokeAllMsg: "Todos los navegadores y dispositivos conectados se cerrarán — incluido este. Deberás iniciar sesión de nuevo aquí justo después.",
        revokeAll: "Revocar todas",
        activeSessions: "Sesiones activas ({n})",
        revokeAllBtn: "Revocar todas",
        loading: "Cargando...",
        noSessions: "No hay sesiones activas",
        unknown: "Desconocido",
        createdLabel: "creada",
        lastUsedLabel: "último uso",
      },
      deleteAccount: {
        soleAdmin: "Eres el único admin de: {names}. Transfiere los derechos de admin o elimina esas organizaciones antes de cerrar tu cuenta.",
        dangerZone: "⚠ Zona de peligro",
        deleteTitle: "Eliminar tu cuenta",
        deleteDesc: "Cierra permanentemente tu cuenta. Tus datos personales (sesiones, configuración de 2FA, membresías) se eliminarán. Los proyectos, hallazgos e informes permanecen en tu organización, pero pierden la atribución a ti. Esto no se puede deshacer.",
        deleteBtn: "Eliminar cuenta",
        confirmTitle: "⚠ Confirmar eliminación de cuenta",
        confirmPre: "Esta acción es permanente. Escribe ",
        confirmMid: "para confirmar, luego ",
        with2fa: "verifica con tu código 2FA",
        withPwd: "ingresa tu contraseña",
        typeToConfirm: "Escribe \"DELETE\" para confirmar",
        deletePlaceholder: "DELETE",
        typeDeleteErr: "Escribe DELETE para confirmar.",
        stepUpReason: "Ingresa tu código 2FA para eliminar permanentemente tu cuenta",
        passwordRequired: "La contraseña es obligatoria.",
        currentPassword: "Contraseña actual",
        deleting: "Eliminando...",
        permanentlyDelete: "Eliminar mi cuenta permanentemente",
        cancel: "Cancelar",
      },
    },
    twoFactor: {
      loading: "Cargando estado del 2FA...",
      backupTitle: "✓ Autenticación de dos factores activada",
      backupDesc: "Guarda estos códigos de respaldo ahora. NO se mostrarán de nuevo. Cada código puede usarse una vez si pierdes acceso a tu app de autenticación.",
      copyClipboard: "📋 Copiar",
      savedThem: "Ya los guardé",
      backupCopied: "Códigos de respaldo copiados",
      backupFileHeader: "Códigos de respaldo del 2FA — TrueHacking",
      backupFileFooter: "Guárdalos en un lugar seguro. Cada código puede usarse solo una vez.",
      scanQr: "Escanea este QR con tu app de autenticación",
      qrAlt: "QR del 2FA",
      enterManually: "O ingresa esta clave manualmente:",
      recommendedApps: "Apps recomendadas: Google Authenticator, Authy, 1Password, Bitwarden",
      step2: "Paso 2 — Ingresa el código de 6 dígitos de tu app",
      verificationCode: "Código de verificación",
      codePlaceholder: "000000",
      busy: "...",
      activate: "Activar 2FA",
      cancel: "Cancelar",
      disableTitle: "⚠ Desactivar autenticación de dos factores",
      disableDesc: "Esto eliminará el 2FA de tu cuenta. Ya no necesitarás un código para iniciar sesión. Recomendamos mantener el 2FA activado — especialmente para cuentas de admin.",
      currentPassword: "Contraseña actual",
      currentCode: "Código 2FA actual (o código de respaldo)",
      disableCodePlaceholder: "000000 o XXXX-XXXX",
      disableBtn: "Desactivar 2FA",
      requiredLabel: "Obligatorio:",
      requiredText: " Las cuentas de superusuario deben activar la autenticación de dos factores antes de acceder a la plataforma. Configura el 2FA abajo para continuar.",
      twoFactorAuth: "Autenticación de dos factores",
      enabled: "Activado",
      disabled: "Desactivado",
      activatedOn: "Activado el",
      backupRemaining: "{n} códigos de respaldo restantes",
      considerRegen: " — considera regenerar",
      disable2faBtn: "Desactivar 2FA",
      enable2faBtn: "Activar 2FA",
      recommendation: "⚠ Recomendación",
      recommendationText: "Activa el 2FA para proteger tu cuenta TrueHacking. Las operaciones de admin en el panel de la plataforma requerirán verificación por 2FA. Escanea un QR con Google Authenticator, Authy, 1Password o cualquier otra app TOTP.",
    },
    billing: {
      loading: "Cargando facturación…",
      unavailable: "La información de facturación no está disponible ahora.",
      portalError: "No se pudo abrir el portal de facturación.",
      currentPlan: "Plan actual",
      billingLabel: "Facturación:",
      accessUntil: "Acceso hasta {date}",
      renews: "Se renueva el {date}",
      freeTier: "Estás en el plan gratuito.",
      changePlan: "Cambiar plan",
      upgrade: "Mejorar plan",
      opening: "Abriendo…",
      manageBilling: "Gestionar facturación",
      portalNote: "Gestionar facturación abre tu portal seguro de Stripe — cambia o cancela tu plan, actualiza tu tarjeta y descarga facturas. Los pagos los procesa Stripe; nunca vemos los datos de tu tarjeta.",
      statuses: {
        active: "Activo",
        trialing: "Prueba",
        past_due: "Vencido",
        canceled: "Cancelado",
        unpaid: "No pagado",
        incomplete: "Incompleto",
      },
      cycles: {
        yearly: "Anual",
        monthly: "Mensual",
      },
    },
    members: {
      noOrg: "Ninguna organización seleccionada",
      roleUpdated: "Rol actualizado",
      failed: "Falló:",
      removeTitle: "¿Eliminar miembro?",
      removeMsg: "{email} perderá acceso a esta organización inmediatamente. Su cuenta personal permanece — solo se revoca la membresía.",
      remove: "Eliminar",
      memberRemoved: "Miembro eliminado",
      membersSuffix: "Miembros",
      membersCount: "{n} miembros",
      cancel: "Cancelar",
      inviteMember: "+ Invitar miembro",
      inviteNew: "Invitar nuevo miembro",
      email: "Correo",
      emailPlaceholder: "usuario@empresa.com",
      fullNameOptional: "Nombre completo (opcional)",
      role: "Rol",
      tempPassword: "Contraseña temporal",
      tempPasswordPlaceholder: "Mín. 8 caracteres",
      busy: "...",
      createMember: "Crear miembro",
      loading: "Cargando miembros...",
      active: "● Activo",
      disabled: "● Desactivado",
      lastLogin: "Último acceso:",
      never: "Nunca",
      colUser: "Usuario",
      colStatus: "Estado",
      colRole: "Rol",
      colLastLogin: "Último acceso",
      roles: {
        admin: "Admin",
        pentester: "Pentester",
        viewer: "Visor",
        client: "Cliente",
      },
    },
    upgrade: {
      title: "Planes y facturación",
      subtitle: "Estás en el plan {plan}.",
      checkoutSuccess: "Suscripción actualizada — tu plan se reflejará en breve.",
      checkoutCancelled: "Checkout cancelado — no se hizo ningún cargo.",
      checkoutError: "No se pudo iniciar el checkout. Intenta de nuevo o contacta a soporte.",
      portalError: "No se pudo abrir el portal de facturación.",
      billing: "Facturación",
      monthly: "Mensual",
      annual: "Anual",
      currency: "Moneda",
      opening: "Abriendo…",
      manageBilling: "Gestionar facturación",
      loadingPlans: "Cargando planes…",
      current: "Actual",
      free: "Gratis",
      custom: "A consultar",
      perYr: " / año",
      perMo: " / mes",
      twoMonthsFree: "2 meses gratis",
      unlimited: "Ilimitado",
      scansLabel: "scans / mes",
      engagementsLabel: "proyectos",
      membersLabel: "miembros del equipo",
      yourPlan: "Tu plan",
      contactSales: "Hablar con ventas",
      choose: "Elegir {name}",
      starting: "Iniciando…",
      footnote: "La facturación anual y los totales en USD (incluido cualquier descuento) se muestran de forma segura en el siguiente paso. Los pagos los procesa Stripe — nunca vemos los datos de tu tarjeta.",
      features: {
        ai_enrichment: "Análisis de riesgo por IA y playbooks de ataque",
        deep_validation: "Validación profunda (explotación activa)",
        ai_senior_pentester: "Nivel de IA pentester sénior",
        sso: "Single sign-on (SSO)",
      },
    },
    scanHistory: {
      title: "Historial de scans",
      subtitle: "Historial de tareas en segundo plano de todos los proyectos",
      errorPrefix: "Error:",
      unknownError: "Error desconocido",
      loading: "Cargando historial de tareas...",
      totalTasks: "Total de tareas",
      activeNow: "Activas ahora",
      activeSub: "en cola + ejecutando",
      idleSub: "inactivo",
      completed: "Completadas",
      failed: "Fallaron",
      noTasks: "No se encontraron tareas",
      colNum: "#",
      colEngagement: "Proyecto",
      colStatus: "Estado",
      colPriority: "Prioridad",
      colPhase: "Fase / Progreso",
      colResults: "Resultados",
      colDuration: "Duración",
      colWhen: "Cuándo",
      retry: "reintento {n}",
      evShort: "ev",
      findShort: "hall.",
      filters: {
        all: "Todas",
        running: "Ejecutando",
        queued: "En cola",
        completed: "Completadas",
        failed: "Fallaron",
        cancelled: "Canceladas",
      },
      statuses: {
        queued: "en cola",
        running: "ejecutando",
        completed: "completado",
        failed: "falló",
        cancelled: "cancelado",
        retrying: "reintentando",
        timeout: "timeout",
      },
      priorities: {
        high: "alta",
        normal: "normal",
        low: "baja",
      },
      phases: {
        passive_recon: "Recon pasivo",
        asset_discovery: "Descubrimiento de activos",
        fingerprinting: "Fingerprinting",
        crawling: "Crawling",
        vuln_scanning: "Escaneo de vulns",
        deep_validation: "Validación profunda",
        correlation: "Correlación",
        ai_enrichment: "Enriquecimiento por IA",
        completed: "Completado",
        failed: "Falló",
        initializing: "Inicializando",
      },
      timeAgo: {
        seconds: "hace {n}s",
        minutes: "hace {n}min",
        hours: "hace {n}h",
      },
    },
    adminPage: {
      platformAdminBadge: "ADMIN DE LA PLATAFORMA",
      stepUpActive: "● Step-up activo",
      unresolvedOne: "⚠ {n} incidente sin resolver",
      unresolvedMany: "⚠ {n} incidentes sin resolver",
      openHealth: "Abrir Salud del Sistema",
      title: "Admin de la plataforma",
      subtitle: "Operaciones de superusuario que afectan a todos los usuarios y organizaciones",
      warning: "⚠ Las acciones en este panel afectan a TODOS los usuarios y organizaciones de la plataforma. Las operaciones destructivas requieren verificación 2FA step-up.",
      sectionAria: "Sección de admin",
      tabs: {
        dashboard: "Panel",
        users: "Usuarios",
        orgs: "Organizaciones",
        tokens: "Tokens",
        settings: "Configuración",
        system: "Sistema",
        health: "Salud",
        blog: "Blog",
      },
      gateSubtitle: "Operaciones de superusuario",
      gateTitle: "Autenticación de dos factores obligatoria",
      gateBody: "El panel de admin de la plataforma requiere que el 2FA esté activado en tu cuenta. Esto es innegociable porque las operaciones de admin pueden afectar a todos los usuarios y organizaciones de la plataforma. Ve a Perfil → Autenticación 2FA para configurarlo.",
    },
    adminStepUp: {
      title: "Autenticación step-up",
      subtitle: "Reverifica el 2FA para operaciones de admin",
      codeLabel: "Código 2FA actual (o código de respaldo)",
      codePlaceholder: "000000 o XXXX-XXXX",
      busy: "...",
      verify: "Verificar",
      cancel: "Cancelar",
      tokenValid: "Token válido por 15 minutos tras la verificación",
    },
    adminDashboard: {
      loading: "Cargando panel...",
      loadError: "Error al cargar el panel.",
      issuesHealth: "Problemas y salud",
      unresolvedAlerts: "alertas de seguridad sin resolver",
      triageHint: "abre la pestaña Salud para triar →",
      scansFailed: "scans fallaron en las últimas 24h",
      failedLogins: "inicios de sesión fallidos en las últimas 24h",
      bruteForce: "posible fuerza bruta",
      jobsBacklogged: "jobs acumulados (scans {scans}, enrich {enrich})",
      diskUsedOn: "de disco usado en {path}",
      allHealthy: "✓ Todos los sistemas saludables.",
      queueLabel: "Cola",
      asOf: "al",
      refresh: "↻ actualizar",
      sectionLive: "En vivo",
      scansToday: "scans hoy",
      scansInFlight: "scans en curso",
      usersOnline15: "usuarios en línea (15m)",
      sectionRevenue: "Ingresos y crecimiento",
      mrr: "MRR",
      newSubsWk: "nuevas subs (sem)",
      canceledWk: "canceladas (sem)",
      trials7d: "trials → 7d",
      planDistribution: "distribución por plan",
      newOrgsWk: "nuevas orgs (sem)",
      newOrgsMo: "nuevas orgs (mes)",
      newUsersWk: "nuevos usuarios (sem)",
      freeToPaid: "gratis → pago",
      sectionUsers: "Usuarios",
      active30d: "activos (30d)",
      mfaEnabled: "MFA activado",
      onlineNow15: "en línea ahora (15m)",
      roleDistribution: "distribución por rol",
      sectionAI: "IA",
      aiTriagedWk: "triados por IA (sem)",
      aiFpRate: "tasa de falso-positivo de la IA",
      sectionTotals: "Totales",
      orgs: "orgs",
      users: "usuarios",
      engagements: "proyectos",
    },
    adminHealth: {
      intro: "Incidentes operativos que la plataforma registró automáticamente. Usa el filtro de cuenta para soporte (\"el cliente X reporta un problema\"). Resolver un elemento solo lo reconoce — no corrige la causa raíz, pero limpia la alerta del panel.",
      accountSearch: "Buscar cuenta",
      accountPlaceholder: "correo o nombre de org",
      status: "Estado",
      statusOpen: "Abiertos",
      statusResolved: "Resueltos",
      statusAll: "Todos",
      statusAria: "Filtro de estado",
      severity: "Severidad",
      sevAny: "Cualquiera",
      sevCritical: "Crítico",
      sevError: "Error",
      sevWarning: "Aviso",
      severityAria: "Filtro de severidad",
      kind: "Tipo",
      kindAny: "Cualquiera",
      kindScanTimeout: "Timeout de scan",
      kindEmailFailure: "Fallo de correo",
      kindAiFailure: "Fallo de IA",
      kindAria: "Filtro de tipo",
      loading: "Cargando incidentes...",
      noIncidents: "Ningún incidente coincide.",
      nothingOnFire: "Nada en llamas.",
      rangeOf: "de",
      prev: "Anterior",
      next: "Siguiente",
      resolved: "✓ resuelto",
      resolve: "Resolver",
      incidentResolved: "Incidente resuelto",
      failed: "Falló",
      hide: "ocultar detalle",
      detail: "detalle",
      whereToLook: "↳ Dónde mirar:",
      kindLabels: {
        scan_timeout: "Timeout de scan",
        resend_failure: "Fallo de correo (Resend)",
        ai_enrichment_failure: "Fallo en el enriquecimiento por IA",
      },
      sevLabels: {
        critical: "Crítico",
        error: "Error",
        warning: "Aviso",
      },
    },
    adminSystem: {
      refreshed: "{n} herramientas actualizadas",
      refreshFailed: "Error al actualizar:",
      checking: "Verificando salud de las herramientas...",
      overallHealth: "Salud general",
      toolsInstalled: "{installed}/{total} herramientas instaladas",
      installed: "Instaladas",
      missing: "Faltan",
      categories: "Categorías",
      checkingBtn: "Verificando...",
      refreshBtn: "⟳ Actualizar",
      lastCheck: "Última verificación:",
      cachedFor: "en caché por",
      missingBadge: "FALTA",
      colTool: "Herramienta",
      colVersion: "Versión",
      colDescError: "Descripción / Error",
      colPath: "Ruta",
      unknown: "desconocida",
      aboutTitle: "Sobre esta página:",
      aboutPre: "las verificaciones se basan en ",
      aboutPost: " para presencia y una llamada rápida de subproceso para la versión (timeout de 3 segundos por herramienta). Los resultados se almacenan en caché por 60 segundos. Esta página solo verifica que los binarios estén instalados y respondiendo — no prueba si funcionan contra objetivos reales. Las herramientas faltantes no son fatales — el orquestador registra una advertencia y las omite, pero tu cobertura sufre silenciosamente.",
      categoryLabels: {
        recon: "Recon pasivo",
        discovery: "Descubrimiento de activos",
        fingerprinting: "Fingerprinting",
        crawling: "Crawling",
        vuln_scanning: "Escaneo de vulnerabilidades",
        deep_validation: "Validación profunda",
      },
    },
    adminSettings: {
      loading: "Cargando configuración...",
      modified: "● modificado",
      noChanges: "Sin cambios",
      settingsSaved: "Configuración guardada",
      saveFailed: "Error al guardar:",
      changesReverted: "Cambios revertidos",
      lastUpdated: "Última actualización:",
      never: "nunca",
      byUser: " por el usuario #{id}",
      revert: "Revertir",
      save: "Guardar ({n} cambios)",
      secRegistration: "▸ Registro",
      regEnabled: "Registro habilitado",
      regEnabledDesc: "Cuando está deshabilitado, los nuevos usuarios no pueden registrarse vía /auth/register. Los flujos OAuth siguen funcionando. Los usuarios existentes no se ven afectados.",
      secOauth: "▸ Proveedores OAuth",
      googleEnabled: "Google OAuth habilitado",
      googleDesc: "Permite a los usuarios iniciar sesión con Google. Requiere credenciales en .env (HYDRA_GOOGLE_OAUTH_CLIENT_ID/SECRET).",
      githubEnabled: "GitHub OAuth habilitado",
      githubDesc: "Permite a los usuarios iniciar sesión con GitHub. Requiere credenciales en .env.",
      secQuotas: "▸ Cuotas",
      maxEng: "Máx. de proyectos por organización",
      maxEngDesc: "Límite estricto de proyectos en una sola org. Usa 0 para ilimitado. Aplicado al crear.",
      unitEng: "proy",
      maxScans: "Máx. de scans por día por usuario",
      maxScansDesc: "Cuota diaria (día de calendario UTC) de ejecuciones de scan por usuario. Usa 0 para ilimitado. Los superusuarios la omiten.",
      unitScans: "scans",
      secAudit: "▸ Retención de log de auditoría",
      auditRetention: "Retención de auditoría (días)",
      auditRetentionDesc: "Cron diario a las 03:00 UTC elimina entradas de auditoría más antiguas que esto. Piso estricto de 30 días aplicado de todas formas (LGPD).",
      unitDays: "días",
      secAI: "▸ Enriquecimiento por IA",
      aiEnabled: "Enriquecimiento por IA habilitado",
      aiDesc: "Usa TrueHacking AI para enriquecer hallazgos con puntuación CVSS, playbooks de ataque y remediación. Deshabilita si el servicio de IA no está disponible o es muy lento.",
      noteTitle: "Nota:",
      noteBody: "Algunas configuraciones (secreto JWT, orígenes CORS, HSTS) intencionalmente NO son editables aquí. Viven en .env / docker-compose.yml y requieren reiniciar el contenedor para cambiar. Esto es un diseño de seguridad deliberado — la modificación en tiempo de ejecución de límites de seguridad vía API es un objetivo de alto valor para cuentas de admin comprometidas.",
    },
    adminTokens: {
      revokeUserTitle: "¿Revocar todas las sesiones del usuario?",
      revokeUserMsg: "{email} se cerrará en todos los dispositivos en su próxima solicitud. La cuenta permanece — solo se eliminan las sesiones activas.",
      revokeSessions: "Revocar sesiones",
      revokedFor: "{n} sesiones revocadas para {email}",
      revokeFailed: "Error al revocar:",
      mustType: "Debes escribir 'REVOKE ALL' exactamente",
      nuclearDone: "☢ {n} sesiones REVOCADAS globalmente — cerrando tu sesión",
      nuclearFailed: "Error en la revocación nuclear:",
      totalTokens: "Total de tokens",
      active: "Activos",
      revoked: "Revocados",
      usersWithSessions: "Usuarios c/ sesiones",
      filterPlaceholder: "Filtrar por user_id (número)...",
      includeRevoked: "Incluir revocados",
      nuclearBtn: "☢ Nuclear: Revocar TODOS",
      nuclearTitle: "☢ OPCIÓN NUCLEAR",
      nuclearDesc: "Esto revocará TODOS los refresh tokens activos de la plataforma. Todos los usuarios — incluido tú — se cerrarán inmediatamente y deberán iniciar sesión de nuevo. Úsalo solo en respuesta a un incidente de seguridad confirmado (sospecha de compromiso del secreto JWT, fuga de credenciales, etc).",
      reasonLabel: "Motivo (registrado en el log de auditoría)",
      reasonPlaceholder: "ej.: Sospecha de compromiso del secreto JWT el 2026-04-08",
      typeToConfirm: "Escribe REVOKE ALL para confirmar:",
      execute: "☢ Ejecutar",
      cancel: "Cancelar",
      loading: "Cargando tokens...",
      noTokens: "Ningún token coincide con los filtros actuales",
      statusRevoked: "● Revocado",
      statusActive: "● Activo",
      uaPrefix: "UA:",
      ipPrefix: "IP:",
      createdPrefix: "Creado:",
      revokeUser: "Revocar usuario",
      idPrefix: "id",
      colUser: "Usuario",
      colUA: "User Agent",
      colIP: "IP",
      colStatus: "Estado",
      colCreated: "Creado",
      prev: "← Anterior",
      next: "Siguiente →",
      rangeOf: "de",
    },
    adminOrgs: {
      orgCreated: "Organización creada",
      createFailed: "Error al crear:",
      forceDeleteTitle: "¿Forzar eliminación de la organización?",
      deleteTitle: "¿Eliminar organización?",
      forceDeleteMsg: "FORZAR ELIMINACIÓN de {name}, incluyendo {n} proyectos + todos los hallazgos, evidencias e historial de scan. Los miembros pierden acceso inmediatamente. Esto no se puede deshacer.",
      deleteMsg: "Eliminar {name}. Falla si la org tiene proyectos activos — usa Forzar eliminación en ese caso.",
      forceDelete: "Forzar eliminación",
      delete: "Eliminar",
      orgDeleted: "Organización eliminada",
      deleteFailed: "Error al eliminar:",
      searchPlaceholder: "Buscar nombre o slug...",
      orgsCount: "{n} orgs",
      cancel: "Cancelar",
      newOrg: "+ Nueva org",
      createTitle: "Crear nueva organización",
      nameLabel: "Nombre",
      namePlaceholder: "Acme Corp",
      descLabel: "Descripción (opcional)",
      createBtn: "Crear organización",
      loading: "Cargando orgs...",
      noOrgs: "No se encontraron organizaciones",
      membersLabel: "miembros",
      engagementsLabel: "proyectos",
      colName: "Nombre",
      colUUID: "UUID",
      colPlan: "Plan",
      colMembers: "Miembros",
      colEngagements: "Proyectos",
      detail: "Detalle →",
      prev: "← Anterior",
      next: "Siguiente →",
      rangeOf: "de",
      membersStat: "Miembros",
      engagementsStat: "Proyectos",
      byStatus: "Por estado",
      membersCount: "Miembros ({n})",
      actionsStepUp: "Acciones (step-up obligatorio)",
      deleteSafe: "Eliminar (seguro)",
      forceDeleteCascade: "🗑 Forzar eliminación (cascada)",
    },
    adminUsers: {
      disableTitle: "¿Deshabilitar usuario?",
      enableTitle: "¿Habilitar usuario?",
      disableMsg: "{email} pierde acceso a la plataforma inmediatamente. Sus sesiones siguen válidas hasta su próxima solicitud, luego caen al login.",
      enableMsg: "{email} recupera acceso a la plataforma inmediatamente.",
      disable: "Deshabilitar",
      enable: "Habilitar",
      userDisabled: "Usuario deshabilitado",
      userEnabled: "Usuario habilitado",
      failed: "Falló:",
      resetTitle: "¿Restablecer contraseña?",
      resetMsg: "{email} recibe una contraseña recién generada y TODAS sus sesiones se revocan. Deberá iniciar sesión de nuevo con la nueva contraseña — que se mostrará una vez y se copiará al portapapeles.",
      resetConfirm: "Restablecer contraseña",
      newPwdTitle: "Nueva contraseña generada",
      newPwdMsg: "Copiada a tu portapapeles:\n\n{pwd}\n\nEsta es la única vez que se mostrará. {n} sesiones revocadas.",
      savedIt: "La guardé",
      pwdReset: "Contraseña restablecida — {n} sesiones revocadas",
      resetFailed: "Error al restablecer:",
      grantTitle: "¿Conceder superusuario?",
      revokeTitle: "¿Revocar superusuario?",
      grantMsg: "{email} OBTENDRÁ acceso de admin de la plataforma — podrá realizar operaciones destructivas en todos los usuarios y organizaciones.",
      revokeMsg: "{email} perderá el acceso de admin de la plataforma — ya no alcanzará el panel de admin.",
      grantConfirm: "Conceder superusuario",
      revokeConfirm: "Revocar superusuario",
      superUpdated: "Flag de superusuario actualizada",
      updateFailed: "Error al actualizar:",
      markVerifiedTitle: "¿Marcar correo como verificado?",
      markVerifiedMsg: "Marca manualmente email_verified=true para {email}. Úsalo solo para casos de soporte (error de tipeo al registrarse, pérdida de acceso al buzón, cuenta pre-gate). La acción se registra en el log de auditoría.",
      markVerifiedConfirm: "Marcar verificado",
      emailMarkedVerified: "Correo marcado como verificado",
      markVerifiedFailed: "Error al marcar verificado:",
      exported: "Datos del usuario exportados (LGPD)",
      exportFailed: "Error al exportar:",
      anonTitle: "¿Anonimizar usuario (borrado LGPD)?",
      anonMsg: "Derecho al borrado para {email}. Esto hará:\n\n• Reemplazar el correo por deleted-user-{id}@anonymized.local\n• Borrar nombre, avatar, credenciales\n• Revocar todas las sesiones\n• Eliminar de todas las organizaciones\n\nEsto es irreversible. El log de auditoría registrará quién lo hizo y cuándo.",
      anonConfirm: "Anonimizar",
      userAnonymized: "Usuario anonimizado",
      anonFailed: "Error al anonimizar:",
      searchPlaceholder: "Buscar correo o nombre...",
      allStatus: "Todos los estados",
      active: "Activos",
      disabled: "Deshabilitados",
      allRoles: "Todos los roles",
      superusers: "Superusuarios",
      regular: "Comunes",
      all2fa: "Todos 2FA",
      twofaEnabledOpt: "2FA activado",
      twofaDisabledOpt: "2FA desactivado",
      usersCount: "{n} usuarios",
      exportCsv: "Exportar CSV",
      exportOptions: "Opciones de exportación",
      applyScreenFilters: "Aplicar filtros de la pantalla",
      includeDisabled: "Incluir deshabilitados",
      includeUnverified: "Incluir correo no verificado",
      exportAlwaysExcludes: "Los superusuarios y las cuentas anonimizadas nunca se exportan.",
      downloadCsv: "Descargar CSV",
      exportingCsv: "Exportando...",
      csvExported: "CSV de usuarios descargado",
      csvExportFailed: "Fallo al exportar CSV:",
      loading: "Cargando usuarios...",
      noUsers: "Ningún usuario coincide con los filtros actuales",
      super: "SUPER",
      statusAnonymized: "● Anonimizado",
      statusActive: "● Activo",
      statusDisabled: "● Deshabilitado",
      emailConfirmed: "Correo confirmado",
      emailNotVerified: "Correo aún no verificado",
      verified: "verificado",
      unverified: "no verificado",
      twofaOn: "activado",
      twofaOff: "desactivado",
      lastLogin: "Último acceso:",
      never: "Nunca",
      colUser: "Usuario",
      colStatus: "Estado",
      colRole: "Rol",
      colVerified: "Verificado",
      col2fa: "2FA",
      colLastLogin: "Último acceso",
      anon: "● Anon",
      emailConfirmedDesk: "Correo confirmado por el usuario",
      emailNotVerifiedDesk: "El usuario aún no ha hecho clic en el enlace de verificación",
      yes: "✓ Sí",
      no: "✗ No",
      onLabel: "✓ Activado",
      offLabel: "Desactivado",
      detail: "Detalle →",
      prev: "← Anterior",
      next: "Siguiente →",
      rangeOf: "de",
      userNum: "Usuario #{id}",
      statusLabel: "Estado",
      superuser: "Superusuario",
      yesShort: "Sí",
      noShort: "No",
      twofa: "2FA",
      enabledShort: "Activado",
      disabledShort: "Desactivado",
      authProvider: "Proveedor de auth",
      activeSessions: "Sesiones activas",
      engagementsCreated: "Proyectos creados",
      lgpdData: "Datos LGPD",
      consent: "Consentimiento:",
      consentVer: "(v{v})",
      preLgpd: "Cuenta pre-LGPD",
      anonymizedAt: "Anonimizado:",
      organizations: "Organizaciones",
      anonNoActions: "Este usuario está anonimizado. No hay más acciones disponibles.",
      actionsStepUp: "Acciones (step-up obligatorio)",
      disableUser: "Deshabilitar usuario",
      enableUser: "Habilitar usuario",
      moreActions: "Más acciones del usuario",
      resetPassword: "Restablecer contraseña",
      revokeSuperuser: "Revocar superusuario",
      grantSuperuser: "Conceder superusuario",
      markEmailVerified: "Marcar correo como verificado",
      exportData: "Exportar datos (LGPD)",
      anonymizeErasure: "Anonimizar (borrado LGPD)",
    },
    adminBlog: {
      newPost: "Nuevo artículo",
      editPost: "Editar artículo",
      noPosts: "Aún no hay artículos.",
      loading: "Cargando...",
      colTitle: "Título",
      colLocale: "Idioma",
      colStatus: "Estado",
      colUpdated: "Actualizado",
      draft: "Borrador",
      published: "Publicado",
      edit: "Editar",
      delete: "Eliminar",
      deleteTitle: "¿Eliminar artículo?",
      deleteMsg: "\"{title}\" se eliminará permanentemente.",
      deleteConfirm: "Eliminar",
      deleted: "Artículo eliminado",
      saved: "Artículo guardado",
      saveFailed: "Error al guardar:",
      fldTitle: "Título",
      fldSlug: "Slug (opcional)",
      slugPlaceholder: "generado del título",
      fldLocale: "Idioma",
      fldExcerpt: "Resumen (opcional)",
      excerptPlaceholder: "Resumen corto para las tarjetas y SEO",
      fldStatus: "Estado",
      fldCover: "Imagen de portada",
      uploadCover: "Subir imagen",
      uploading: "Subiendo...",
      removeCover: "Quitar",
      uploadFailed: "Error al subir",
      fldBody: "Contenido",
      cancel: "Cancelar",
      save: "Guardar",
      saving: "Guardando...",
      stepUpSave: "Confirma tu 2FA para guardar el artículo",
      stepUpDelete: "Confirma tu 2FA para eliminar el artículo",
      stepUpUpload: "Confirma tu 2FA para subir la imagen",
    },
    ui: {
      confirmDelete: "Eliminar",
      confirm: "Confirmar",
      cancel: "Cancelar",
      dismiss: "Descartar",
      teaserPreparing: "El análisis de IA se está preparando. Esto normalmente tarda un par de minutos tras completarse el scan (o tras verificar la propiedad del dominio).",
      teaserRefresh: "Actualizar",
      verifyOwnership: "Verificar propiedad del dominio",
      upgradeUnlock: "Mejorar plan para desbloquear",
      preview: "(vista previa)",
      premiumFeature: "🔒 Función premium",
      upgradeTitle: "Mejora tu plan",
      upgradeBody: "Esta función está disponible en los planes pagos. Los pagos aún no están activos — todavía estamos puliendo el flujo de facturación.",
      upgradeContact: "¿Quieres acceso anticipado? Ponte en contacto:",
      gotIt: "Entendido",
    },
  },
};

export const DICTIONARIES: Record<Locale, Dictionary> = { pt, en, es };
